import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  ArrowClockwise, Warning, PaperPlaneTilt, X as XIcon, Sparkle,
  UploadSimple, MapPin, Truck, Envelope, FileCsv, ChatCircleText,
  Trash, MagnifyingGlass, ArrowUUpLeft, Cloud, Lightning,
  CheckCircle, XCircle, Copy, Stethoscope, Files, ArrowsClockwise,
} from "@phosphor-icons/react";
import client from "../lib/api";
import { useAuth } from "../context/AuthContext";
import FirstColdEmailWizard from "../components/FirstColdEmailWizard";

const TONE_OPTIONS = [
  { value: "warm", label: "Warm", desc: "Casual, trucker-to-trucker" },
  { value: "direct", label: "Direct", desc: "Lead with the pain point" },
  { value: "urgent", label: "Urgent", desc: "Setup slots this week are limited" },
];

const DEFAULT_REPLY_TO = "contact@freightpilotai.ai";

// ---------------------------------------------------------------------------
// Deliverability Doctor — the P0 tool that surfaces WHY cold emails
// disappear into spam. Checks SPF/DKIM/DMARC on the sending domain and lets
// admin push a real test email to their own inbox. Sitting at the top of
// this page so it's the first thing seen after a "0% open rate" moment.
// ---------------------------------------------------------------------------
function DeliverabilityDoctor({ adminEmail }) {
  const [checking, setChecking] = useState(false);
  const [sending, setSending] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [report, setReport] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [seedResult, setSeedResult] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const [copiedKey, setCopiedKey] = useState("");
  const [showSeedForm, setShowSeedForm] = useState(false);
  const [seedRecipients, setSeedRecipients] = useState("");

  const runDnsCheck = async () => {
    setChecking(true);
    setTestResult(null);
    try {
      const { data } = await client.get("/admin/leads/deliverability/dns");
      setReport(data);
      if (data.overall === "healthy") {
        toast.success("Your sending domain is fully authenticated");
      } else if (data.overall === "partial") {
        toast.warning("DNS partially set. Fix the flagged records below.");
      } else {
        toast.error("DNS is broken — this is why cold emails go to spam.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "DNS check failed");
    } finally {
      setChecking(false);
    }
  };

  const sendTest = async () => {
    setSending(true);
    try {
      const { data } = await client.post("/admin/leads/deliverability/test-send", {});
      setTestResult(data);
      setReport(data.dns);
      if (data.sent) {
        toast.success(`Test email sent to ${data.recipient}. Check inbox AND spam folder.`);
      } else {
        toast.error(`Test send failed: ${data.reason || "unknown"}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test send failed");
    } finally {
      setSending(false);
    }
  };

  const runSeedTest = async () => {
    const emails = seedRecipients
      .split(/[\s,;]+/)
      .map((e) => e.trim().toLowerCase())
      .filter((e) => e && e.includes("@"));
    if (emails.length === 0) {
      toast.error("Add at least one email (separate with commas or newlines).");
      return;
    }
    if (emails.length > 5) {
      toast.error("Max 5 inboxes per seed test.");
      return;
    }
    setSeeding(true);
    try {
      const { data } = await client.post("/admin/leads/warmup/seed-test", {
        recipients: emails,
      });
      setSeedResult(data);
      if (data.sent === data.total) {
        toast.success(`✅ Seeded ${data.sent}/${data.total} inboxes. Check each for Inbox vs Spam.`);
      } else {
        toast.warning(`Seeded ${data.sent}/${data.total} — some failed`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Seed test failed");
    } finally {
      setSeeding(false);
    }
  };

  const copyValue = (key, value) => {
    navigator.clipboard.writeText(value).then(() => {
      setCopiedKey(key);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopiedKey(""), 1500);
    });
  };

  const statusPill = (status) => {
    if (!status) return null;
    const s = status.toLowerCase();
    const pass = s === "pass" || s.startsWith("pass_");
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${
          pass
            ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30"
            : "bg-red-500/15 text-red-400 border border-red-500/30"
        }`}
      >
        {pass ? <CheckCircle size={10} weight="fill" /> : <XCircle size={10} weight="fill" />}
        {status.replace("_", " ")}
      </span>
    );
  };

  const overallVerdict = report?.overall;

  return (
    <div
      className="card-surface p-5 border border-[#FF5A1F]/30 bg-gradient-to-br from-[#FF5A1F]/[0.06] to-transparent"
      data-testid="deliverability-doctor-card"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#FF5A1F]/15 border border-[#FF5A1F]/40 flex items-center justify-center shrink-0">
            <Stethoscope size={20} weight="fill" className="text-[#FF5A1F]" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-white leading-tight">
              Deliverability Doctor
            </h2>
            <p className="text-xs text-neutral-400 mt-1 max-w-md">
              Diagnose why cold emails land in spam. Checks SPF / DKIM / DMARC on your sending domain in ~2 seconds.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="deliverability-check-dns-btn"
            onClick={runDnsCheck}
            disabled={checking || sending}
            className="text-xs font-semibold px-3 py-2 rounded-md bg-white/[0.05] border border-white/10 hover:border-white/25 text-white disabled:opacity-50 transition-colors flex items-center gap-1.5"
          >
            {checking ? (
              <ArrowClockwise size={12} className="animate-spin" />
            ) : (
              <Lightning size={12} weight="fill" />
            )}
            {checking ? "Checking…" : "Check DNS"}
          </button>
          <button
            data-testid="deliverability-test-send-btn"
            onClick={sendTest}
            disabled={sending || checking}
            className="text-xs font-semibold px-3 py-2 rounded-md bg-[#FF5A1F] hover:bg-[#FF7038] text-black disabled:opacity-50 transition-colors flex items-center gap-1.5"
          >
            {sending ? (
              <ArrowClockwise size={12} className="animate-spin" />
            ) : (
              <PaperPlaneTilt size={12} weight="fill" />
            )}
            {sending ? "Sending…" : "Send test to me"}
          </button>
          <button
            data-testid="deliverability-seed-btn"
            onClick={() => setShowSeedForm((v) => !v)}
            disabled={sending || checking || seeding}
            className="text-xs font-semibold px-3 py-2 rounded-md bg-white/[0.05] border border-white/10 hover:border-white/25 text-neutral-300 disabled:opacity-50 flex items-center gap-1.5"
            title="Send 3-5 test emails to different inboxes"
          >
            <Truck size={12} weight="fill" />
            Seed inboxes
          </button>
        </div>
      </div>

      {/* Seed inbox form — expands when the button is clicked */}
      {showSeedForm && (
        <div
          className="mt-3 p-3 rounded-lg border border-white/10 bg-white/[0.02]"
          data-testid="seed-inbox-form"
        >
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">
            Warmup seed test
          </div>
          <div className="text-[11px] text-neutral-400 mb-2 leading-relaxed">
            Send 1-5 test emails through the same Gmail SMTP / Resend path
            as real cold sends. Best practice: seed a personal Gmail, an
            Outlook, a Yahoo, and check where each lands.
          </div>
          <textarea
            value={seedRecipients}
            onChange={(e) => setSeedRecipients(e.target.value)}
            placeholder="you@gmail.com&#10;you@outlook.com&#10;you@yahoo.com"
            rows={3}
            data-testid="seed-recipients-input"
            className="w-full bg-white/[0.03] border border-white/10 rounded-md text-white text-xs py-1.5 px-2.5 focus:outline-none focus:border-[#FF5A1F]/60 font-mono"
          />
          <div className="flex items-center justify-between mt-2">
            <div className="text-[10px] text-neutral-500">
              1–5 emails · comma / newline separated
            </div>
            <button
              data-testid="seed-run-btn"
              onClick={runSeedTest}
              disabled={seeding}
              className="rounded-md px-3 py-1.5 text-xs font-semibold bg-[#FF5A1F] text-black hover:bg-[#FF7038] disabled:opacity-50 flex items-center gap-1.5"
            >
              {seeding ? (
                <ArrowClockwise size={11} className="animate-spin" />
              ) : (
                <Truck size={11} weight="fill" />
              )}
              {seeding ? "Sending…" : "Seed now"}
            </button>
          </div>

          {seedResult && (
            <div className="mt-3 space-y-1" data-testid="seed-result">
              <div className="text-[11px] font-semibold text-white">
                Result: {seedResult.sent}/{seedResult.total} sent
              </div>
              {seedResult.results?.map((r, i) => (
                <div key={i} className="flex items-center gap-2 text-[11px]">
                  {r.sent ? (
                    <CheckCircle size={11} weight="fill" className="text-emerald-400" />
                  ) : (
                    <XCircle size={11} weight="fill" className="text-red-400" />
                  )}
                  <span className="text-neutral-300 font-mono">{r.recipient}</span>
                  <span className="text-neutral-500">
                    {r.provider}{r.reason ? ` · ${r.reason}` : ""}
                  </span>
                </div>
              ))}
              <div className="text-[10px] text-neutral-400 mt-2 italic">
                {seedResult.next_step}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Verdict banner */}
      {report && (
        <div
          className={`mt-4 px-4 py-3 rounded-md border text-sm flex items-center gap-2 ${
            overallVerdict === "healthy"
              ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300"
              : overallVerdict === "partial"
              ? "bg-yellow-500/10 border-yellow-500/30 text-yellow-300"
              : "bg-red-500/10 border-red-500/30 text-red-300"
          }`}
          data-testid="deliverability-verdict"
        >
          {overallVerdict === "healthy" ? (
            <CheckCircle size={16} weight="fill" />
          ) : (
            <Warning size={16} weight="fill" />
          )}
          <span className="font-semibold uppercase tracking-wider text-[11px]">
            {overallVerdict === "healthy"
              ? "Domain fully authenticated"
              : overallVerdict === "partial"
              ? "Domain partially set — some records missing"
              : "Domain not authenticated — this is why 0 opens"}
          </span>
          <button
            onClick={() => setExpanded((v) => !v)}
            className="ml-auto text-[10px] text-neutral-400 underline hover:text-white"
            data-testid="deliverability-toggle-details"
          >
            {expanded ? "Hide details" : "Show details"}
          </button>
        </div>
      )}

      {/* Test send result */}
      {testResult && testResult.sent && (
        <div
          className="mt-3 px-4 py-3 rounded-md bg-blue-500/10 border border-blue-500/30 text-blue-200 text-sm"
          data-testid="deliverability-test-result"
        >
          <div className="font-semibold mb-1">Test email sent to {testResult.recipient}</div>
          <div className="text-xs text-blue-300/80">
            👉 Check your Inbox. Also check Spam/Promotions. If it's not in Inbox, the DNS records below need fixing.
          </div>
        </div>
      )}

      {/* Records table */}
      {report && expanded && (
        <div className="mt-4 space-y-3" data-testid="deliverability-records">
          {[
            { key: "spf", label: "SPF", desc: "Authorizes who can send mail as your domain" },
            { key: "dkim", label: "DKIM", desc: "Cryptographic signature proving the email is real" },
            { key: "dmarc", label: "DMARC", desc: "Policy for what to do when SPF/DKIM fail" },
          ].map(({ key, label, desc }) => {
            const rec = report[key] || {};
            const isMissing = rec.status === "missing" || rec.status === "misconfigured";
            const recc = report.recommend?.[key];
            return (
              <div
                key={key}
                className="rounded-lg border border-white/10 bg-white/[0.02] p-3"
                data-testid={`deliverability-row-${key}`}
              >
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-white">{label}</span>
                      {statusPill(rec.status)}
                    </div>
                    <div className="text-[11px] text-neutral-500 mt-0.5">{desc}</div>
                  </div>
                </div>
                {rec.record && (
                  <div className="mt-2 font-mono text-[11px] text-neutral-400 bg-black/40 px-2 py-1.5 rounded break-all">
                    {rec.record}
                  </div>
                )}
                {isMissing && recc && (
                  <div className="mt-3 p-3 rounded-md bg-[#FF5A1F]/8 border border-[#FF5A1F]/25">
                    <div className="text-[11px] font-semibold text-[#FF5A1F] mb-2 uppercase tracking-wider">
                      👉 Add this DNS record
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-[11px]">
                      <div>
                        <div className="text-neutral-500 mb-1">Type</div>
                        <code className="text-white bg-black/40 px-1.5 py-0.5 rounded">{recc.type}</code>
                      </div>
                      <div>
                        <div className="text-neutral-500 mb-1">Host / Name</div>
                        <code className="text-white bg-black/40 px-1.5 py-0.5 rounded">{recc.host}</code>
                      </div>
                      <div className="relative">
                        <div className="text-neutral-500 mb-1">Value</div>
                        <button
                          onClick={() => copyValue(key, recc.value)}
                          className="absolute right-0 top-0 text-[10px] text-neutral-400 hover:text-white flex items-center gap-1"
                          data-testid={`deliverability-copy-${key}`}
                        >
                          <Copy size={10} />
                          {copiedKey === key ? "Copied" : "Copy"}
                        </button>
                        <code className="text-white bg-black/40 px-1.5 py-0.5 rounded block break-all mt-0.5">
                          {recc.value}
                        </code>
                      </div>
                    </div>
                    <div className="text-[10px] text-neutral-400 mt-2 italic">
                      {recc.why}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          <div className="text-[11px] text-neutral-500 leading-relaxed p-3 rounded-md bg-white/[0.02] border border-white/5">
            <b className="text-neutral-400">Where do I add these?</b> Log into your domain registrar (GoDaddy, Namecheap, Cloudflare, etc.), open DNS management for <code className="text-neutral-300">freightpilotai.ai</code>, and add each missing TXT record above. Changes take 5-30 minutes to propagate.
          </div>
        </div>
      )}

      {!report && (
        <div className="mt-3 text-[11px] text-neutral-500">
          👆 Click "Check DNS" to diagnose in 2 seconds, or "Send test to me" to receive a live test email at <code className="text-neutral-300">{adminEmail || DEFAULT_REPLY_TO}</code>.
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ReputationBadge — 0-100 sender score with band (green/amber/red/unknown).
// Rendered inline in the WarmupPanel header. Loads from
// GET /admin/leads/warmup/reputation. Click the badge to expand a
// 30-day sparkline sourced from /admin/leads/warmup/reputation/history.
// ---------------------------------------------------------------------------
function ReputationBadge({ reputation }) {
  const [expanded, setExpanded] = useState(false);
  const [history, setHistory] = useState(null);
  const [loadingHist, setLoadingHist] = useState(false);

  const loadHistory = async () => {
    setLoadingHist(true);
    try {
      const { data } = await client.get("/admin/leads/warmup/reputation/history?days=30");
      setHistory(data);
    } catch (e) {
      /* silent */
    } finally {
      setLoadingHist(false);
    }
  };

  const toggle = () => {
    const nextOpen = !expanded;
    setExpanded(nextOpen);
    if (nextOpen && !history) loadHistory();
  };

  if (!reputation) return null;
  const { score, band, components, sample_size, message } = reputation;
  const cfg = {
    green: { cls: "bg-emerald-500/15 border-emerald-500/40 text-emerald-300", dot: "bg-emerald-400", label: "HEALTHY" },
    amber: { cls: "bg-yellow-500/15 border-yellow-500/40 text-yellow-300", dot: "bg-yellow-400", label: "OK" },
    red: { cls: "bg-red-500/15 border-red-500/40 text-red-300", dot: "bg-red-400 animate-pulse", label: "DANGER" },
    unknown: { cls: "bg-neutral-700/40 border-white/10 text-neutral-400", dot: "bg-neutral-500", label: "WARMING" },
  };
  const c = cfg[band] || cfg.unknown;
  const scoreText = score === null || score === undefined ? "—" : `${score}`;
  const tooltip = [
    message,
    `Sample: ${sample_size} sends (last 30 days)`,
    `Open ${components.open_rate_pct}% · Reply ${components.reply_rate_pct}% · Bounce ${components.bounce_rate_pct}%`,
  ].join("\n");
  return (
    <div className="relative">
      <button
        type="button"
        data-testid="warmup-reputation-badge"
        onClick={toggle}
        className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-[11px] font-semibold cursor-pointer transition-colors hover:brightness-110 ${c.cls}`}
        title={tooltip}
      >
        <span className={`w-2 h-2 rounded-full ${c.dot}`} data-testid={`reputation-dot-${band}`} />
        <span className="tracking-widest">REPUTATION</span>
        <span className="text-white text-sm font-bold" data-testid="reputation-score">{scoreText}</span>
        <span className="text-[9px] opacity-80" data-testid={`reputation-band-${band}`}>{c.label}</span>
        <span className="text-[9px] opacity-60 ml-1">{expanded ? "▲" : "▼"}</span>
      </button>
      {expanded && (
        <ReputationSparklinePanel
          history={history}
          loading={loadingHist}
          current={reputation}
          onClose={() => setExpanded(false)}
        />
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// ReputationSparklinePanel — 30-day bar chart with fixed 0-100 y-axis.
// Green ≥80, amber 50-79, red <50, empty for missing days. Displayed as
// a floating card below the badge.
// ---------------------------------------------------------------------------
function ReputationSparklinePanel({ history, loading, current, onClose }) {
  const series = history?.series || [];
  const hasAny = series.some((d) => d.score !== null && d.score !== undefined);

  const scoreBar = (d, idx) => {
    if (d.score === null || d.score === undefined) {
      return (
        <div
          key={idx}
          data-testid={`sparkline-bar-${idx}`}
          title={`${d.day} · no data`}
          className="flex-1 min-w-[3px] bg-white/[0.04] rounded-sm"
          style={{ height: "10%" }}
        />
      );
    }
    const s = Math.max(0, Math.min(100, d.score));
    const cls = s >= 80
      ? "bg-emerald-400/80"
      : s >= 50
        ? "bg-yellow-400/80"
        : "bg-red-400/80";
    return (
      <div
        key={idx}
        data-testid={`sparkline-bar-${idx}`}
        title={`${d.day} · score ${s} (${d.band || "n/a"})`}
        className={`flex-1 min-w-[3px] rounded-sm ${cls} hover:brightness-125`}
        style={{ height: `${Math.max(4, s)}%` }}
      />
    );
  };

  return (
    <div
      data-testid="reputation-sparkline-panel"
      className="absolute z-30 top-full mt-2 right-0 w-[340px] rounded-lg border border-white/10 bg-black/95 backdrop-blur p-4 shadow-2xl"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] uppercase tracking-widest text-neutral-500">Reputation · last 30 days</div>
        <button
          type="button"
          onClick={onClose}
          data-testid="sparkline-close"
          className="text-neutral-500 hover:text-white text-xs"
        >
          ✕
        </button>
      </div>

      {loading ? (
        <div className="text-xs text-neutral-500 py-6 text-center" data-testid="sparkline-loading">
          Loading history…
        </div>
      ) : !hasAny ? (
        <div className="text-xs text-neutral-500 py-6 text-center leading-relaxed" data-testid="sparkline-empty">
          No snapshots yet. Snapshots capture daily at 7am ET —
          <br />or click <b className="text-white">Snapshot now</b> below.
        </div>
      ) : (
        <>
          <div
            className="flex items-end gap-[2px] h-24 border-b border-white/5 pb-1"
            data-testid="sparkline-bars"
          >
            {series.map((d, idx) => scoreBar(d, idx))}
          </div>
          <div className="flex items-center justify-between text-[9px] text-neutral-500 mt-1">
            <span>{series[0]?.day?.slice(5)}</span>
            <span>Today</span>
          </div>
        </>
      )}

      <div className="mt-3 flex items-center gap-3 flex-wrap text-[9px]">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-emerald-400/80" /> ≥80</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-yellow-400/80" /> 50–79</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-red-400/80" /> &lt;50</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-white/[0.04]" /> no data</span>
      </div>

      <SnapshotNowButton />
    </div>
  );
}

function SnapshotNowButton() {
  const [busy, setBusy] = useState(false);
  const [captured, setCaptured] = useState(false);
  const capture = async () => {
    setBusy(true);
    try {
      await client.post("/admin/leads/warmup/reputation/snapshot-now");
      setCaptured(true);
      toast.success("Snapshot captured — reload to see it on the sparkline");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Snapshot failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={capture}
      disabled={busy || captured}
      data-testid="sparkline-snapshot-now"
      className="mt-3 w-full text-[10px] font-semibold px-3 py-1.5 rounded-md bg-white/[0.03] border border-white/10 hover:border-white/25 text-neutral-300 disabled:opacity-50"
    >
      {busy ? "Capturing…" : captured ? "✓ Captured" : "Snapshot now"}
    </button>
  );
}


// ---------------------------------------------------------------------------
// Safe Warmup panel — schedules a paced cold-outreach drip (5-10/day)
// across Tue-Thu 9am-2pm ET business hours instead of blasting. This is the
// deliverability-safe way to rebuild sender rep before ramping to volume.
// ---------------------------------------------------------------------------
function WarmupPanel({ selectedIds, onScheduled }) {
  const [perDay, setPerDay] = useState(8);
  const [tone, setTone] = useState("warm");
  const [scheduling, setScheduling] = useState(false);
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [schedule, setSchedule] = useState(null);
  const [reputation, setReputation] = useState(null);

  const loadStatus = async () => {
    setStatusLoading(true);
    try {
      const [s, sched, rep] = await Promise.all([
        client.get("/admin/leads/warmup/status"),
        client.get("/admin/leads/warmup/schedule?days=14"),
        client.get("/admin/leads/warmup/reputation"),
      ]);
      setStatus(s.data);
      setSchedule(sched.data);
      setReputation(rep.data);
    } catch (e) {
      // silent — panel just doesn't show status
    } finally {
      setStatusLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scheduleWarmup = async () => {
    if (selectedIds.size === 0) {
      toast.error("Select some leads first (checkbox on the left of each row).");
      return;
    }
    setScheduling(true);
    try {
      const { data } = await client.post("/admin/leads/warmup/start", {
        lead_ids: Array.from(selectedIds),
        per_day: perDay,
        tone,
      });
      const days = data.total_days || 1;
      toast.success(`Scheduled ${data.scheduled} leads over ~${days} day${days === 1 ? "" : "s"} at ${data.per_day}/day`);
      loadStatus();
      onScheduled?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to schedule warmup");
    } finally {
      setScheduling(false);
    }
  };

  const cancelAll = async () => {
    if (!window.confirm("Cancel ALL pending warmup sends? This can't be undone.")) return;
    setCanceling(true);
    try {
      const { data } = await client.post("/admin/leads/warmup/cancel", {});
      toast.success(`Canceled ${data.canceled} pending sends`);
      loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    } finally {
      setCanceling(false);
    }
  };

  const nextSend = status?.upcoming?.[0]?.scheduled_for;
  const nextSendLabel = nextSend
    ? new Date(nextSend).toLocaleString("en-US", {
        weekday: "short", month: "short", day: "numeric",
        hour: "numeric", minute: "2-digit", timeZoneName: "short",
      })
    : null;

  const estFinish = perDay > 0 && selectedIds.size > 0
    ? Math.ceil(selectedIds.size / perDay)
    : 0;

  return (
    <div
      className="card-surface p-5 border border-blue-500/25 bg-gradient-to-br from-blue-500/[0.05] to-transparent"
      data-testid="warmup-panel"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-500/15 border border-blue-500/40 flex items-center justify-center shrink-0">
            <Cloud size={20} weight="fill" className="text-blue-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-white leading-tight">Safe Warmup Sender</h2>
            <p className="text-xs text-neutral-400 mt-1 max-w-lg">
              Instead of blasting, drip 5-12 emails per day across Tue-Thu 9am-2pm ET. Rebuilds sender reputation so cold emails actually land in inbox.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <ReputationBadge reputation={reputation} />
          <button
            data-testid="warmup-refresh-status-btn"
            onClick={loadStatus}
            disabled={statusLoading}
            className="text-xs text-neutral-400 hover:text-white flex items-center gap-1"
            title="Refresh queue status"
          >
            <ArrowClockwise size={12} className={statusLoading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {/* Live queue status strip */}
      {status && (
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="rounded-md bg-white/[0.03] border border-white/10 px-3 py-2" data-testid="warmup-stat-pending">
            <div className="text-[9px] uppercase tracking-widest text-neutral-500">Pending</div>
            <div className="text-xl font-semibold text-blue-300">{status.pending}</div>
          </div>
          <div className="rounded-md bg-white/[0.03] border border-white/10 px-3 py-2" data-testid="warmup-stat-sent">
            <div className="text-[9px] uppercase tracking-widest text-neutral-500">Sent</div>
            <div className="text-xl font-semibold text-emerald-400">{status.sent}</div>
          </div>
          <div className="rounded-md bg-white/[0.03] border border-white/10 px-3 py-2" data-testid="warmup-stat-failed">
            <div className="text-[9px] uppercase tracking-widest text-neutral-500">Failed</div>
            <div className="text-xl font-semibold text-red-400">{status.failed}</div>
          </div>
          <div className="rounded-md bg-white/[0.03] border border-white/10 px-3 py-2">
            <div className="text-[9px] uppercase tracking-widest text-neutral-500">Next</div>
            <div className="text-xs font-medium text-white truncate" title={nextSendLabel || "—"}>
              {nextSendLabel || "—"}
            </div>
          </div>
        </div>
      )}

      {/* 14-day schedule strip — the Warmup Schedule Visualizer.
          Each cell shows one day's queued+sent sends. In-window days
          (Tue/Wed/Thu) are highlighted; off-window days are dimmed. */}
      {schedule && schedule.schedule && (
        <ScheduleStrip schedule={schedule.schedule} />
      )}

      {/* Schedule form */}
      <div className="mt-4 rounded-lg border border-white/10 bg-white/[0.02] p-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1 block">Per Day</label>
            <select
              value={perDay}
              onChange={(e) => setPerDay(parseInt(e.target.value, 10))}
              data-testid="warmup-per-day"
              className="w-full bg-white/[0.03] border border-white/10 rounded-lg text-white text-sm py-2 px-3 focus:outline-none focus:border-blue-400/60"
            >
              <option value={3}>3 / day (very safe)</option>
              <option value={5}>5 / day (safe)</option>
              <option value={8}>8 / day (recommended)</option>
              <option value={10}>10 / day (aggressive)</option>
              <option value={12}>12 / day (max)</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1 block">Tone</label>
            <select
              value={tone}
              onChange={(e) => setTone(e.target.value)}
              data-testid="warmup-tone"
              className="w-full bg-white/[0.03] border border-white/10 rounded-lg text-white text-sm py-2 px-3 focus:outline-none focus:border-blue-400/60"
            >
              <option value="warm">Warm · Trucker-to-trucker</option>
              <option value="direct">Direct · Lead with pain</option>
              <option value="urgent">Urgent · Limited pilot spots</option>
            </select>
          </div>
          <button
            data-testid="warmup-schedule-btn"
            onClick={scheduleWarmup}
            disabled={scheduling || selectedIds.size === 0}
            className="rounded-lg py-2 px-4 text-sm font-semibold bg-blue-500 hover:bg-blue-400 text-black disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
          >
            {scheduling ? (
              <>
                <ArrowClockwise size={14} className="animate-spin" /> Scheduling…
              </>
            ) : (
              <>
                <Cloud size={14} weight="fill" /> Schedule {selectedIds.size} lead{selectedIds.size === 1 ? "" : "s"}
              </>
            )}
          </button>
        </div>

        {/* Preview */}
        <div className="mt-3 text-[11px] text-neutral-400 flex flex-wrap items-center gap-x-3 gap-y-1">
          {selectedIds.size === 0 ? (
            <span data-testid="warmup-hint-select">
              👉 Select leads with the checkboxes below, then schedule the drip.
            </span>
          ) : (
            <>
              <span data-testid="warmup-preview">
                Will finish in <b className="text-white">~{estFinish} business day{estFinish === 1 ? "" : "s"}</b> ·
                {" "}business hours only (Tue-Thu 9am-2pm ET)
              </span>
              {status && status.pending > 0 && (
                <span className="text-yellow-300/80">
                  ({status.pending} already pending — new sends queue AFTER them)
                </span>
              )}
            </>
          )}
        </div>
      </div>

      {/* Cancel all + Auto-refill toggle */}
      {status && status.pending > 0 && (
        <div className="mt-3 flex items-center justify-end">
          <button
            data-testid="warmup-cancel-all-btn"
            onClick={cancelAll}
            disabled={canceling}
            className="text-[11px] text-red-300 hover:text-red-200 underline disabled:opacity-50"
          >
            {canceling ? "Canceling…" : `Cancel all ${status.pending} pending`}
          </button>
        </div>
      )}

      <AutoRefillPanel />
    </div>
  );
}

// ---------------------------------------------------------------------------
// AutoRefillPanel — toggle + settings for the warmup auto-refill scheduler.
// When enabled, the hourly background task will top up the warmup queue from
// fresh FMCSA leads matching the saved filter when pending drops below
// threshold. Keeps the drip alive without manual scheduling.
// ---------------------------------------------------------------------------
function AutoRefillPanel() {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await client.get("/admin/leads/warmup/refill-prefs");
      setPrefs(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (payload) => {
    setSaving(true);
    try {
      const { data } = await client.put("/admin/leads/warmup/refill-prefs", payload);
      setPrefs(data);
      toast.success(data.enabled ? "Auto-refill ON" : "Auto-refill OFF");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !prefs) return null;

  return (
    <div
      className="mt-3 rounded-lg border border-white/10 bg-white/[0.02] p-3"
      data-testid="auto-refill-panel"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              data-testid="auto-refill-toggle"
              checked={prefs.enabled}
              onChange={(e) => save({ ...prefs, enabled: e.target.checked })}
              disabled={saving}
              className="rounded"
            />
            <ArrowsClockwise size={13} weight="bold" className={prefs.enabled ? "text-emerald-400" : "text-neutral-500"} />
            <span className="text-sm text-white">Auto-refill queue</span>
            {prefs.enabled && (
              <span className="text-[10px] uppercase tracking-widest bg-emerald-500/15 text-emerald-400 border border-emerald-500/40 rounded-full px-2 py-0.5 font-semibold">
                LIVE
              </span>
            )}
          </label>
        </div>
        <button
          onClick={() => setExpanded((v) => !v)}
          className="text-[11px] text-neutral-400 hover:text-white"
          data-testid="auto-refill-config-toggle"
        >
          {expanded ? "Hide config" : "Config"}
        </button>
      </div>
      <div className="text-[11px] text-neutral-500 mt-1">
        When pending drops below <b className="text-neutral-300 tabular-nums">{prefs.threshold}</b>, top up with <b className="text-neutral-300 tabular-nums">{prefs.batch_size}</b> fresh leads from FMCSA
        {prefs.filter_state && (
          <> (state <b className="text-neutral-300">{prefs.filter_state}</b>)</>
        )}
        {(prefs.filter_min_trucks || prefs.filter_max_trucks) && (
          <> ({prefs.filter_min_trucks || 0}–{prefs.filter_max_trucks || "∞"} trucks)</>
        )}.
      </div>

      {expanded && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3">
          <ConfigField label="Threshold" value={prefs.threshold}
            onChange={(v) => save({ ...prefs, threshold: parseInt(v, 10) || 20 })}
            testid="refill-threshold" />
          <ConfigField label="Batch size" value={prefs.batch_size}
            onChange={(v) => save({ ...prefs, batch_size: parseInt(v, 10) || 20 })}
            testid="refill-batch-size" />
          <ConfigField label="Per day" value={prefs.per_day}
            onChange={(v) => save({ ...prefs, per_day: parseInt(v, 10) || 8 })}
            testid="refill-per-day" />
          <ConfigField label="Filter state" value={prefs.filter_state || ""}
            placeholder="TX"
            onChange={(v) => save({ ...prefs, filter_state: v })}
            testid="refill-filter-state" />
          <ConfigField label="Min trucks" value={prefs.filter_min_trucks || ""}
            placeholder="3"
            onChange={(v) => save({ ...prefs, filter_min_trucks: v ? parseInt(v, 10) : null })}
            testid="refill-min-trucks" />
          <ConfigField label="Max trucks" value={prefs.filter_max_trucks || ""}
            placeholder="25"
            onChange={(v) => save({ ...prefs, filter_max_trucks: v ? parseInt(v, 10) : null })}
            testid="refill-max-trucks" />
        </div>
      )}
    </div>
  );
}

function ConfigField({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="text-[9px] uppercase tracking-widest text-neutral-500 mb-0.5 block">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full bg-white/[0.03] border border-white/10 rounded-md text-white text-xs py-1.5 px-2 focus:outline-none focus:border-blue-400/60"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// HotRepliesStrip — surfaces incoming replies from leads at the very top
// of Admin → Cold Leads. Loads on mount + auto-refresh after user runs a
// Gmail IMAP scan. "New" replies (seen=false) get a red pulsing dot.
// ---------------------------------------------------------------------------
/* Per-row call outcome logger — hot-opener leads get a 📞 dropdown; picking
   an outcome logs it and follow-ups write themselves server-side. */
function CallOutcomeSelect({ lead }) {
  const [logged, setLogged] = useState(lead.last_call_outcome || "");
  const [busy, setBusy] = useState(false);
  const [askDate, setAskDate] = useState(false);
  const [demoDate, setDemoDate] = useState(() =>
    new Date(Date.now() + 86400000).toISOString().slice(0, 10));
  const [demoTime, setDemoTime] = useState("");

  const logOutcome = async (outcome, demo_date = "", demo_time = "") => {
    if (!outcome) return;
    setBusy(true);
    try {
      await client.post(`/admin/leads/${lead.id}/call-outcome`, { outcome, demo_date, demo_time });
      setLogged(outcome);
      const msg = {
        booked_demo: "Demo booked 🎉 — they'll get a reminder email the morning of",
        not_interested: "Logged — follow-ups canceled",
        callback: "Logged — callback follow-up email scheduled for tomorrow",
        voicemail: "Logged — voicemail follow-up email scheduled (+2d)",
        no_answer: "Logged — try-again email scheduled (+2d)",
      }[outcome] || "Call logged";
      toast.success(msg);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not log call");
    } finally {
      setBusy(false);
    }
  };

  if (askDate) {
    return (
      <span className="flex items-center gap-1">
        <input
          type="date"
          value={demoDate}
          onChange={(e) => setDemoDate(e.target.value)}
          data-testid={`lead-demo-date-${lead.email}`}
          className="text-[10px] bg-neutral-900 border border-white/15 rounded-full px-2 py-1 text-neutral-300 focus:outline-none"
          title="When is the demo? A reminder email goes out that morning"
        />
        <input
          type="time"
          value={demoTime}
          onChange={(e) => setDemoTime(e.target.value)}
          data-testid={`lead-demo-time-${lead.email}`}
          className="text-[10px] bg-neutral-900 border border-white/15 rounded-full px-2 py-1 text-neutral-300 focus:outline-none"
          title="Optional time (CT) — you'll get a text 30 min before once SMS is live"
        />
        <button
          onClick={() => { setAskDate(false); logOutcome("booked_demo", demoDate, demoTime); }}
          data-testid={`lead-demo-date-confirm-${lead.email}`}
          className="text-[10px] px-2 py-1 rounded-full bg-[#FF5A1F] text-black font-semibold hover:bg-[#FF7038]"
        >
          Book
        </button>
        <button
          onClick={() => setAskDate(false)}
          data-testid={`lead-demo-date-cancel-${lead.email}`}
          className="text-[10px] text-neutral-500 hover:text-white px-1"
        >
          ✕
        </button>
      </span>
    );
  }
  if (logged) {
    const label = {
      booked_demo: "📅 Demo booked", callback: "📞 Callback set",
      voicemail: "📞 Voicemail", no_answer: "📞 No answer",
      not_interested: "🚫 Not interested",
    }[logged] || `📞 ${logged}`;
    return (
      <span className="text-[10px] px-2 py-1 rounded-full bg-white/5 border border-white/15 text-neutral-300 font-semibold" data-testid={`lead-call-logged-${lead.email}`}>
        {label}
      </span>
    );
  }
  return (
    <select
      defaultValue=""
      disabled={busy}
      onChange={(e) => (e.target.value === "booked_demo" ? setAskDate(true) : logOutcome(e.target.value))}
      data-testid={`lead-call-outcome-${lead.email}`}
      className="text-[10px] bg-neutral-900 border border-white/15 rounded-full px-2 py-1 text-neutral-300 cursor-pointer hover:border-orange-500/50 focus:outline-none"
      title="Log what happened on the call — follow-ups write themselves"
    >
      <option value="" disabled>📞 Log call…</option>
      <option value="booked_demo">📅 Booked demo</option>
      <option value="callback">🔁 Asked to call back</option>
      <option value="voicemail">📼 Left voicemail</option>
      <option value="no_answer">🔇 No answer</option>
      <option value="not_interested">🚫 Not interested</option>
    </select>
  );
}


// Demo Prep Card — one-glance fleet basics + history before a demo call.
function DemoPrepModal({ leadId, onClose }) {
  const [data, setData] = useState(null);
  const [points, setPoints] = useState(null);
  const [objections, setObjections] = useState(null);
  const [pointsErr, setPointsErr] = useState(false);
  const [notes, setNotes] = useState("");
  const [notesSaved, setNotesSaved] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  useEffect(() => {
    client.get(`/admin/leads/${leadId}/prep`)
      .then(({ data }) => {
        setData(data);
        setNotes(data.lead.demo_notes || "");
        setNotesSaved(data.lead.demo_notes || "");
      })
      .catch(() => { toast.error("Could not load prep card"); onClose(); });
    client.get(`/admin/leads/${leadId}/talking-points`)
      .then(({ data }) => { setPoints(data.points); setObjections(data.objections || []); })
      .catch(() => setPointsErr(true));
    // eslint-disable-next-line
  }, [leadId]);

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await client.post(`/admin/leads/${leadId}/demo-notes`, { notes });
      setNotesSaved(notes);
      toast.success("Notes saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save notes");
    } finally {
      setSavingNotes(false);
    }
  };
  const icon = { interested: "🟢", maybe: "🟡", not_interested: "🔴", auto_reply: "🤖" };
  const day = (iso) => (iso || "").slice(0, 10);
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="demo-prep-modal" onClick={onClose}>
      <div className="bg-[#0A0A0A] border border-white/10 rounded-2xl max-w-lg w-full max-h-[80vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
        {!data ? (
          <div className="text-neutral-500 text-sm p-6 text-center">Loading prep card…</div>
        ) : (
          <>
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">📋 DEMO PREP</div>
                <h3 className="font-display text-lg font-semibold text-white">
                  {data.lead.dba_name || data.lead.legal_name}
                  {data.lead.interested && <span className="ml-2 text-xs text-emerald-400">🔥 interested</span>}
                </h3>
              </div>
              <button onClick={onClose} data-testid="demo-prep-close" className="text-neutral-500 hover:text-white text-lg px-1">✕</button>
            </div>
            <div className="flex flex-wrap gap-2 mb-4">
              <span className="text-[11px] px-2.5 py-1 rounded-full bg-white/[0.04] border border-white/10 text-neutral-300">🚚 {data.lead.power_units ?? "?"} trucks</span>
              <span className="text-[11px] px-2.5 py-1 rounded-full bg-white/[0.04] border border-white/10 text-neutral-300">📍 {data.lead.phy_city || "?"}, {data.lead.phy_state || "?"}</span>
              {data.lead.phone && <a href={`tel:${data.lead.phone}`} className="text-[11px] px-2.5 py-1 rounded-full bg-[#FF5A1F]/10 border border-[#FF5A1F]/30 text-[#FF9764] font-semibold">📞 {data.lead.phone}</a>}
              {data.lead.demo_date && <span className="text-[11px] px-2.5 py-1 rounded-full bg-white/[0.04] border border-white/10 text-neutral-300">📅 demo {data.lead.demo_date}{data.lead.demo_time ? ` · ${data.lead.demo_time}` : ""}</span>}
            </div>
            <div className="mb-4 p-3 rounded-xl bg-[#FF5A1F]/[0.06] border border-[#FF5A1F]/20" data-testid="demo-prep-talking-points">
              <div className="text-[10px] uppercase tracking-widest text-[#FF9764] mb-1.5">💬 Talking points</div>
              {points ? points.map((p, i) => (
                <div key={i} className="text-xs text-neutral-200 py-0.5">• {p}</div>
              )) : pointsErr ? (
                <div className="text-xs text-neutral-500">Couldn't generate — reopen to retry</div>
              ) : (
                <div className="text-xs text-neutral-500 animate-pulse">Thinking about what to open with…</div>
              )}
              {objections && objections.length > 0 && (
                <div className="mt-2.5 pt-2.5 border-t border-[#FF5A1F]/15" data-testid="demo-prep-objections">
                  <div className="text-[10px] uppercase tracking-widest text-[#FF9764] mb-1.5">🛡 Likely objections</div>
                  {objections.map((o, i) => (
                    <div key={i} className="text-xs py-0.5">
                      <span className="text-neutral-400">"{o.objection}"</span>
                      <span className="text-neutral-200"> → {o.comeback}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-4 text-sm">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1.5">Emails sent ({data.emails.length})</div>
                {data.emails.length === 0 ? <div className="text-neutral-600 text-xs">None yet</div> : data.emails.map((e, i) => (
                  <div key={i} className="flex items-center justify-between gap-2 py-1 border-b border-white/[0.04] text-xs">
                    <span className="text-neutral-300 truncate">{e.subject || "(no subject)"}{e.is_followup && <span className="text-neutral-600 ml-1">· follow-up</span>}</span>
                    <span className="text-neutral-500 shrink-0">{day(e.created_at)} · {e.open_count || 0}× opened</span>
                  </div>
                ))}
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1.5">Their replies ({data.replies.length})</div>
                {data.replies.length === 0 ? <div className="text-neutral-600 text-xs">No replies yet</div> : data.replies.map((r, i) => (
                  <div key={i} className="py-1 border-b border-white/[0.04] text-xs text-neutral-300">
                    {icon[r.sentiment] || "⚪"} {(r.snippet || r.subject || "").slice(0, 140)}
                  </div>
                ))}
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1.5">Call history ({data.calls.length})</div>
                {data.calls.length === 0 ? <div className="text-neutral-600 text-xs">No calls logged</div> : data.calls.map((c, i) => (
                  <div key={i} className="flex items-center justify-between gap-2 py-1 border-b border-white/[0.04] text-xs">
                    <span className="text-neutral-300">{{ booked_demo: "📅 Booked demo", callback: "🔁 Callback", voicemail: "📼 Voicemail", no_answer: "🔇 No answer", not_interested: "🚫 Not interested" }[c.outcome] || c.outcome}</span>
                    <span className="text-neutral-500 shrink-0">{day(c.created_at)}</span>
                  </div>
                ))}
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1.5">📝 Demo notes</div>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  maxLength={2000}
                  placeholder="Jot what they care about, pricing questions, next steps…"
                  data-testid="demo-prep-notes"
                  className="w-full bg-neutral-900 border border-white/10 rounded-xl px-3 py-2 text-xs text-white placeholder:text-neutral-600 focus:outline-none focus:border-[#FF5A1F]/50 resize-y"
                />
                <button
                  onClick={saveNotes}
                  disabled={savingNotes || notes === notesSaved}
                  data-testid="demo-prep-notes-save"
                  className="mt-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold bg-[#FF5A1F] text-black hover:bg-[#FF7038] disabled:opacity-40 transition-colors"
                >
                  {savingNotes ? "Saving…" : notes === notesSaved ? "Saved" : "Save notes"}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Demo Calendar Strip — upcoming booked demos + past ones awaiting a result.
function DemoStrip() {
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [prepId, setPrepId] = useState(null);

  const load = async () => {
    try {
      const { data } = await client.get("/admin/leads/upcoming-demos");
      setData(data);
    } catch (e) { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const logResult = async (lead, showed) => {
    setBusyId(lead.id);
    try {
      const { data: r } = await client.post(`/admin/leads/${lead.id}/demo-result`, { showed });
      toast.success(showed ? "Marked showed 🎉" :
        r.reschedule_sent ? "No-show logged — friendly reschedule email sent to them" : "No-show logged");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not log result");
    } finally {
      setBusyId(null);
    }
  };

  if (!data || (data.upcoming.length === 0 && data.awaiting_result.length === 0)) return null;
  const name = (l) => l.dba_name || l.legal_name || "Unknown";
  return (
    <div className="card-surface p-4" data-testid="demo-strip">
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-3">📅 DEMO CALENDAR</div>
      <div className="flex flex-wrap gap-2">
        {data.upcoming.map((l) => (
          <span
            key={l.id}
            data-testid={`demo-chip-${l.email}`}
            className={`inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full border font-medium ${
              l.demo_date === data.today
                ? "bg-[#FF5A1F]/15 text-[#FF9764] border-[#FF5A1F]/40"
                : "bg-white/[0.03] text-neutral-300 border-white/10"}`}
          >
            {l.demo_date === data.today ? "🔥 TODAY" : l.demo_date}{l.demo_time ? ` · ${l.demo_time}` : ""}
            <button
              onClick={() => setPrepId(l.id)}
              data-testid={`demo-prep-open-${l.email}`}
              className="text-white font-semibold hover:text-[#FF9764] hover:underline"
              title="Open demo prep card"
            >
              {name(l)}
            </button>
            {l.phone && <a href={`tel:${l.phone}`} className="text-[#FF9764] hover:underline">{l.phone}</a>}
          </span>
        ))}
        {data.awaiting_result.map((l) => (
          <span
            key={l.id}
            data-testid={`demo-awaiting-${l.email}`}
            className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full border border-yellow-500/30 bg-yellow-500/10 text-yellow-300 font-medium"
          >
            {l.demo_date} · <button onClick={() => setPrepId(l.id)} data-testid={`demo-prep-open-past-${l.email}`} className="font-semibold hover:underline">{name(l)}</button> — did they show?
            <button
              disabled={busyId === l.id}
              onClick={() => logResult(l, true)}
              data-testid={`demo-showed-${l.email}`}
              className="px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/30 font-semibold"
            >
              ✅ Showed
            </button>
            <button
              disabled={busyId === l.id}
              onClick={() => logResult(l, false)}
              data-testid={`demo-noshow-${l.email}`}
              className="px-2 py-0.5 rounded-full bg-red-500/15 text-red-300 border border-red-500/40 hover:bg-red-500/25 font-semibold"
              title="Logs no-show and auto-emails them a friendly reschedule note"
            >
              👻 No-show
            </button>
          </span>
        ))}
      </div>
      {prepId && <DemoPrepModal leadId={prepId} onClose={() => setPrepId(null)} />}
    </div>
  );
}

// Win Story Rotation — up to 5 one-line wins, follow-ups rotate through them.
function WinStoryEditor() {
  const [text, setText] = useState("");
  const [saved, setSaved] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    client.get("/admin/leads/win-story")
      .then(({ data }) => {
        const t = (data.stories || []).join("\n");
        setText(t); setSaved(t);
      })
      .catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const stories = text.split("\n").map((s) => s.trim()).filter(Boolean).slice(0, 5);
      const { data } = await client.post("/admin/leads/win-story", { stories });
      const t = (data.stories || []).join("\n");
      setText(t); setSaved(t);
      toast.success(stories.length > 1
        ? `Saved ${stories.length} wins — follow-ups rotate through them`
        : "Saved — every AI follow-up email will weave this in");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card-surface p-4 flex flex-col sm:flex-row sm:items-start gap-3" data-testid="win-story-card">
      <div className="shrink-0">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500">🏆 WIN STORIES</div>
        <div className="text-[11px] text-neutral-600 mt-0.5 max-w-[180px]">Up to 5, one per line — follow-ups rotate so no two sound alike</div>
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={2}
        placeholder={'e.g. "An Ohio owner-op booked 3 loads in his first week"\n"A 6-truck TX fleet cut invoicing from hours to minutes"'}
        data-testid="win-story-input"
        className="flex-1 bg-neutral-900 border border-white/10 rounded-xl px-4 py-2 text-sm text-white placeholder:text-neutral-600 focus:outline-none focus:border-[#FF5A1F]/50 resize-y"
      />
      <button
        onClick={save}
        disabled={busy || text === saved}
        data-testid="win-story-save"
        className="rounded-full px-4 py-2 text-xs font-semibold bg-[#FF5A1F] text-black hover:bg-[#FF7038] disabled:opacity-40 transition-colors shrink-0"
      >
        {busy ? "Saving…" : text === saved ? "Saved" : "Save"}
      </button>
    </div>
  );
}


function HotRepliesStrip({ onLeadClick }) {
  const [replies, setReplies] = useState([]);
  const [unseenTotal, setUnseenTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [showSmsPanel, setShowSmsPanel] = useState(false);
  const [sentimentFilter, setSentimentFilter] = useState("all"); // all|interested|maybe|not_interested|auto_reply|unclassified
  const [sentimentCounts, setSentimentCounts] = useState(null);

  const load = async (filter = sentimentFilter) => {
    setLoading(true);
    try {
      const params = filter && filter !== "all"
        ? `?limit=25&sentiment=${encodeURIComponent(filter)}`
        : `?limit=25`;
      const { data } = await client.get(`/admin/leads/replies${params}`);
      setReplies(data.replies || []);
      setUnseenTotal(data.unseen_total || 0);
    } catch (e) {
      // silent — new endpoint, may not exist on older backends
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const { data } = await client.get("/admin/leads/replies/templates");
      setTemplates(data.templates || []);
    } catch (e) {
      /* silent */
    }
  };

  const loadCounts = async () => {
    try {
      const { data } = await client.get("/admin/leads/replies/sentiment-counts");
      setSentimentCounts(data);
    } catch (e) {
      /* silent */
    }
  };

  useEffect(() => {
    load();
    loadTemplates();
    loadCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeFilter = (f) => {
    setSentimentFilter(f);
    load(f);
  };

  const scanGmail = async () => {
    setScanning(true);
    try {
      const { data } = await client.post("/admin/leads/replies/scan", {
        lookback_days: 14,
        mark_replied: true,
      });
      if (!data.scanned) {
        toast.warning(data.message || "Gmail not connected — connect via Settings first");
      } else if (data.new_replies === 0) {
        toast.success("Scan complete — no new replies found");
      } else {
        toast.success(`Found ${data.new_replies} new repl${data.new_replies === 1 ? "y" : "ies"}!`);
      }
      load();
      loadCounts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gmail scan failed");
    } finally {
      setScanning(false);
    }
  };

  const markAllSeen = async () => {
    try {
      await client.post("/admin/leads/replies/mark-all-seen", {});
      toast.success("Marked all replies as seen");
      load();
      loadCounts();
    } catch (e) {
      toast.error("Failed to mark seen");
    }
  };

  const markOneSeen = async (reply) => {
    try {
      await client.post(`/admin/leads/replies/${reply.id}/mark-seen`);
      setReplies((prev) => prev.map((r) => (r.id === reply.id ? { ...r, seen: true } : r)));
      setUnseenTotal((prev) => Math.max(0, prev - (reply.seen ? 0 : 1)));
    } catch (e) {
      /* silent */
    }
  };

  if (!loading && replies.length === 0 && unseenTotal === 0) {
    // Compact empty-state card — still shows the "Scan Gmail" button so
    // the user can trigger the first-time scan.
    return (
      <div
        className="card-surface p-4 border border-white/10"
        data-testid="hot-replies-empty"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
              <Envelope size={16} weight="fill" className="text-emerald-400" />
            </div>
            <div>
              <div className="text-sm font-semibold text-white">Hot Replies</div>
              <div className="text-[11px] text-neutral-500">
                No lead replies detected yet. Scan your Gmail inbox to check.
              </div>
            </div>
          </div>
          <button
            data-testid="hot-replies-scan-btn"
            onClick={scanGmail}
            disabled={scanning}
            className="text-xs font-semibold px-3 py-2 rounded-md bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25 disabled:opacity-50 flex items-center gap-1.5"
          >
            {scanning ? (
              <>
                <ArrowClockwise size={12} className="animate-spin" /> Scanning…
              </>
            ) : (
              <>
                <ArrowClockwise size={12} weight="bold" /> Scan Gmail
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="card-surface p-5 border border-emerald-500/25 bg-gradient-to-br from-emerald-500/[0.05] to-transparent"
      data-testid="hot-replies-card"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center shrink-0 relative">
            <Envelope size={20} weight="fill" className="text-emerald-400" />
            {unseenTotal > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center animate-pulse">
                {unseenTotal > 9 ? "9+" : unseenTotal}
              </span>
            )}
          </div>
          <div>
            <h2 className="text-lg font-semibold text-white leading-tight">
              Hot Replies
              {unseenTotal > 0 && (
                <span className="ml-2 text-[10px] uppercase tracking-widest text-red-400">
                  {unseenTotal} NEW
                </span>
              )}
            </h2>
            <p className="text-xs text-neutral-400 mt-1">
              Auto-detected replies from cold leads. Click a row to jump to their record.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="hot-replies-sms-settings-btn"
            onClick={() => setShowSmsPanel((v) => !v)}
            className="text-xs font-semibold px-3 py-2 rounded-md bg-white/[0.03] border border-white/10 hover:border-white/25 text-neutral-300 flex items-center gap-1.5"
            title="Configure SMS alerts"
          >
            <ChatCircleText size={12} weight="fill" />
            SMS
          </button>
          <button
            data-testid="hot-replies-scan-btn"
            onClick={scanGmail}
            disabled={scanning}
            className="text-xs font-semibold px-3 py-2 rounded-md bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25 disabled:opacity-50 flex items-center gap-1.5"
          >
            {scanning ? (
              <ArrowClockwise size={12} className="animate-spin" />
            ) : (
              <ArrowClockwise size={12} weight="bold" />
            )}
            {scanning ? "Scanning…" : "Scan Gmail"}
          </button>
          {unseenTotal > 0 && (
            <button
              data-testid="hot-replies-mark-all-seen-btn"
              onClick={markAllSeen}
              className="text-xs text-neutral-400 hover:text-white px-2"
            >
              Mark all seen
            </button>
          )}
          <button
            onClick={() => setCollapsed((v) => !v)}
            className="text-neutral-500 hover:text-white text-xs"
            data-testid="hot-replies-toggle"
          >
            {collapsed ? "Show" : "Hide"}
          </button>
        </div>
      </div>

      {/* SMS alert settings panel — toggle from the SMS button above */}
      {showSmsPanel && (
        <SmsAlertPrefsPanel onClose={() => setShowSmsPanel(false)} />
      )}

      {/* Sentiment filter chips — Wave 6 */}
      {sentimentCounts && sentimentCounts.total > 0 && (
        <div className="mt-4 flex items-center gap-2 flex-wrap" data-testid="sentiment-filter-chips">
          <span className="text-[10px] uppercase tracking-widest text-neutral-500 mr-1">Filter</span>
          {[
            { key: "all", label: "All", count: sentimentCounts.total, cls: "border-white/15 text-neutral-300" },
            { key: "interested", label: "🟢 Interested", count: sentimentCounts.by_sentiment.interested, cls: "border-emerald-500/40 text-emerald-300" },
            { key: "maybe", label: "🟡 Maybe", count: sentimentCounts.by_sentiment.maybe, cls: "border-yellow-500/40 text-yellow-300" },
            { key: "not_interested", label: "🔴 Not interested", count: sentimentCounts.by_sentiment.not_interested, cls: "border-red-500/40 text-red-300" },
            { key: "auto_reply", label: "⚪ Auto", count: sentimentCounts.by_sentiment.auto_reply, cls: "border-white/10 text-neutral-400" },
            { key: "unclassified", label: "· Unclassified", count: sentimentCounts.by_sentiment.unclassified, cls: "border-white/10 text-neutral-400" },
          ].filter((c) => c.key === "all" || c.count > 0).map((c) => {
            const active = sentimentFilter === c.key;
            return (
              <button
                key={c.key}
                data-testid={`sentiment-filter-${c.key}`}
                onClick={() => changeFilter(c.key)}
                className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-colors ${
                  active
                    ? "bg-[#FF5A1F]/15 border-[#FF5A1F]/60 text-[#FF9764]"
                    : `bg-white/[0.02] hover:bg-white/[0.05] ${c.cls}`
                }`}
              >
                {c.label} <span className="text-neutral-500 ml-1">{c.count}</span>
              </button>
            );
          })}
        </div>
      )}

      {!collapsed && (
        <div className="mt-4 divide-y divide-white/[0.05] max-h-96 overflow-y-auto scrollbar-thin">
          {replies.length === 0 && loading && (
            <div className="text-xs text-neutral-500 py-3">Loading…</div>
          )}
          {replies.length === 0 && !loading && sentimentFilter !== "all" && (
            <div className="text-xs text-neutral-500 py-4 text-center" data-testid="sentiment-filter-empty">
              No replies match this filter. <button className="underline hover:text-white" onClick={() => changeFilter("all")}>Clear filter</button>
            </div>
          )}
          {replies.map((r) => (
            <HotReplyRow
              key={r.id}
              reply={r}
              templates={templates}
              onOpen={() => { markOneSeen(r); onLeadClick?.(r.lead_id); }}
              onMarkSeen={() => markOneSeen(r)}
              onRefresh={load}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SmsAlertPrefsPanel — inline settings panel for the Hot Replies SMS alerts.
// Loads current prefs, lets admin toggle enabled + save phone, and offers
// a "Send test SMS" button to verify Twilio is reachable.
// ---------------------------------------------------------------------------
function SmsAlertPrefsPanel({ onClose }) {
  const [enabled, setEnabled] = useState(false);
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await client.get("/admin/leads/replies/alert-prefs");
        setEnabled(!!data.enabled);
        setPhone(data.phone || "");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await client.put("/admin/leads/replies/alert-prefs", { enabled, phone });
      toast.success(enabled ? "SMS alerts ON" : "SMS alerts OFF");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await client.post("/admin/leads/replies/alert-test");
      if (data.sent) {
        toast.success(`Test SMS sent to ${data.phone}`);
      } else {
        toast.error(data.reason === "not_configured"
          ? "Twilio credentials not configured on the server"
          : `Test failed: ${data.reason || "unknown"}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally {
      setTesting(false);
    }
  };

  return (
    <div
      className="mt-3 p-4 rounded-lg border border-white/10 bg-white/[0.02]"
      data-testid="sms-alert-panel"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] uppercase tracking-widest text-neutral-500 font-semibold">
          Reply alert SMS
        </div>
        <button onClick={onClose} className="text-neutral-500 hover:text-white text-xs">
          Close
        </button>
      </div>
      {loading ? (
        <div className="text-xs text-neutral-500">Loading…</div>
      ) : (
        <>
          <div className="flex items-center gap-3 flex-wrap">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                data-testid="sms-alert-enabled"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                className="rounded"
              />
              <span className="text-sm text-white">Enable SMS alerts</span>
            </label>
            <input
              type="tel"
              data-testid="sms-alert-phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+15551234567"
              className="flex-1 min-w-[200px] bg-white/[0.03] border border-white/10 rounded-md text-white text-sm py-1.5 px-2.5 focus:outline-none focus:border-emerald-400/60"
            />
            <button
              data-testid="sms-alert-save"
              onClick={save}
              disabled={saving}
              className="rounded-md px-3 py-1.5 text-xs font-semibold bg-emerald-500 hover:bg-emerald-400 text-black disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save"}
            </button>
            <button
              data-testid="sms-alert-test"
              onClick={sendTest}
              disabled={testing || !phone}
              className="rounded-md px-3 py-1.5 text-xs bg-white/[0.05] border border-white/10 text-neutral-300 hover:text-white disabled:opacity-50"
            >
              {testing ? "Sending…" : "Test"}
            </button>
          </div>
          <div className="text-[11px] text-neutral-500 mt-2 leading-relaxed">
            When new replies are detected (via manual Scan Gmail or the 6am nightly auto-scan), you'll get a text with the reply count + top companies. E.164 format required (e.g. +15551234567).
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SentimentBadge — small colored pill showing GPT-classified reply intent.
// Rendered inline on HotReplyRow so the founder can triage 20 replies in
// 30 seconds by dot color alone.
// ---------------------------------------------------------------------------
function SentimentBadge({ sentiment }) {
  const CFG = {
    interested: { label: "Interested", cls: "bg-emerald-500/15 border-emerald-500/40 text-emerald-400", emoji: "🟢" },
    maybe: { label: "Maybe", cls: "bg-yellow-500/15 border-yellow-500/40 text-yellow-300", emoji: "🟡" },
    not_interested: { label: "Not interested", cls: "bg-red-500/15 border-red-500/40 text-red-300", emoji: "🔴" },
    auto_reply: { label: "Auto-reply", cls: "bg-neutral-700/40 border-white/10 text-neutral-400", emoji: "⚪" },
  };
  const cfg = CFG[sentiment];
  if (!cfg) return null;
  return (
    <span
      className={`text-[9px] uppercase tracking-widest border rounded-full px-1.5 py-0.5 font-semibold ${cfg.cls}`}
      data-testid={`sentiment-${sentiment}`}
      title={`Auto-classified: ${cfg.label}`}
    >
      {cfg.emoji} {cfg.label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// HotReplyRow — a single row in the Hot Replies strip, with an inline
// "Reply-Ready Draft" expander. On expand, GPT-5.2 pre-drafts a 2-line
// response and shows it with Approve+Send / Edit / Regenerate controls.
// ---------------------------------------------------------------------------
function HotReplyRow({ reply, templates = [], onOpen, onMarkSeen, onRefresh }) {
  const isNew = !reply.seen;
  const [showDraft, setShowDraft] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [draftSubject, setDraftSubject] = useState(reply.suggested_reply_subject || "");
  const [draftBody, setDraftBody] = useState(reply.suggested_reply_body || "");
  const [alreadySent, setAlreadySent] = useState(!!reply.response_sent_at);
  // Wave 16 · Reply Coaching Modal: opens whenever the AI grades the
  // last outbound reply below 50 so the VA sees the coaching moment
  // BEFORE the next reply, not just at the Monday digest.
  const [coachingQuality, setCoachingQuality] = useState(null);
  // Wave 16 · Preview grade before sending — VA can click "Grade my
  // draft" to see the score inline BEFORE hitting send, so they can
  // iterate on tone until they clear the 80+ bar.
  const [previewQuality, setPreviewQuality] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  // Wave 16 · Draft Score Streak Widget — bumped every time a grade
  // completes so the mini sparkline refetches the latest history.
  const [streakRefresh, setStreakRefresh] = useState(0);

  const receivedLabel = reply.received_at
    ? new Date(reply.received_at).toLocaleString("en-US", {
        month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
      })
    : "?";

  const generateDraft = async () => {
    setDrafting(true);
    try {
      const { data } = await client.post(`/admin/leads/replies/${reply.id}/draft-response`);
      setDraftSubject(data.subject || "");
      setDraftBody(data.body || "");
      setShowDraft(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Draft generation failed");
    } finally {
      setDrafting(false);
    }
  };

  const sendDraft = async () => {
    if (!draftSubject.trim() || !draftBody.trim()) {
      toast.error("Subject and body are required.");
      return;
    }
    if (!window.confirm(`Send this reply to ${reply.lead_email}?`)) return;
    setSending(true);
    try {
      const { data } = await client.post(
        `/admin/leads/replies/${reply.id}/send-response`,
        { subject: draftSubject, body: draftBody },
      );
      if (data.status === "sent") {
        toast.success(`✅ Sent to ${reply.lead_email}`);
        setAlreadySent(true);
        setShowDraft(false);
        // Reply Quality coaching moment — if the AI scored the outbound
        // reply below 50, surface the rationale + tags immediately so
        // the VA can adjust tone on the NEXT reply, not just after the
        // weekly digest.
        if (data.quality && typeof data.quality.score === "number" && data.quality.score < 50) {
          setCoachingQuality(data.quality);
        }
        onRefresh?.();
      } else {
        toast.error(data.error || "Send failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const gradePreview = async () => {
    if (!draftBody.trim() && !draftSubject.trim()) {
      toast.error("Write a subject or body first");
      return;
    }
    setPreviewing(true);
    setPreviewQuality(null);
    try {
      const { data } = await client.post(
        `/admin/leads/replies/${reply.id}/grade-preview`,
        { subject: draftSubject, body: draftBody },
      );
      // API returns {quality: {score, rationale, tags}} — or {quality: null}
      // if LLM is down / key is missing. Show a friendly note in the latter.
      if (data?.quality) {
        setPreviewQuality(data.quality);
        // Refetch the streak sparkline so the new score lands in the
        // "78 → 82 → 88 ↑" mini chart in real time.
        setStreakRefresh((n) => n + 1);
      } else {
        toast.error("Grading unavailable right now — try again in a moment");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Grading failed");
    } finally {
      setPreviewing(false);
    }
  };


  const toggleDraft = () => {
    if (showDraft) {
      setShowDraft(false);
      return;
    }
    setShowDraft(true);
    if (!draftBody) {
      // First-time expansion → auto-draft
      generateDraft();
    }
  };

  const applyTemplate = async (templateId) => {
    setShowTemplatePicker(false);
    try {
      const { data } = await client.post(
        `/admin/leads/replies/templates/${templateId}/apply/${reply.id}`
      );
      setDraftSubject(data.subject || "");
      setDraftBody(data.body || "");
      setShowDraft(true);
      toast.success(`Applied "${data.template_name}"`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Template apply failed");
    }
  };

  return (
    <div
      data-testid={`hot-reply-${reply.id}`}
      className={`py-2.5 ${isNew ? "bg-red-500/[0.03]" : ""}`}
    >
      <div className="flex items-center gap-3 rounded px-2 -mx-2 hover:bg-white/[0.02]">
        {(() => {
          const s = reply.sentiment;
          if (isNew) {
            return <span className="w-2 h-2 rounded-full bg-red-400 shrink-0 animate-pulse" title="Unseen" />;
          }
          if (s === "interested") {
            return <span className="w-2 h-2 rounded-full bg-emerald-400 shrink-0" title="Interested" />;
          }
          if (s === "maybe") {
            return <span className="w-2 h-2 rounded-full bg-yellow-400 shrink-0" title="Maybe" />;
          }
          if (s === "not_interested") {
            return <span className="w-2 h-2 rounded-full bg-red-500 shrink-0" title="Not interested" />;
          }
          if (s === "auto_reply") {
            return <span className="w-2 h-2 rounded-full bg-neutral-500 shrink-0" title="Auto-reply" />;
          }
          return <span className="w-2 h-2 rounded-full bg-neutral-700 shrink-0" />;
        })()}
        <div
          className="flex-1 min-w-0 cursor-pointer"
          onClick={onOpen}
        >
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-sm font-semibold text-white truncate">
              {reply.company || reply.lead_email}
            </div>
            <span className="text-[10px] text-neutral-500">·</span>
            <div className="text-[11px] text-neutral-400 truncate">{reply.lead_email}</div>
            {reply.power_units && (
              <span className="text-[10px] bg-white/[0.05] border border-white/10 rounded-full px-1.5 py-0.5 text-neutral-400 tabular-nums">
                {reply.power_units} 🚛
              </span>
            )}
            {alreadySent && (
              <span className="text-[9px] uppercase tracking-widest bg-emerald-500/15 border border-emerald-500/40 text-emerald-400 rounded-full px-1.5 py-0.5 font-semibold">
                Replied
              </span>
            )}
            {reply.sentiment && (
              <SentimentBadge sentiment={reply.sentiment} />
            )}
            {reply.suggested_reply_source?.startsWith("auto_rule:") && (
              <span
                className="text-[9px] uppercase tracking-widest bg-blue-500/15 border border-blue-500/40 text-blue-300 rounded-full px-1.5 py-0.5 font-semibold"
                title="This reply matched an auto-rule"
              >
                AUTO-RULE
              </span>
            )}
          </div>
          <div className="text-[11px] text-neutral-400 mt-0.5 truncate italic">
            "{reply.subject || "(no subject)"}"
          </div>
        </div>
        <div className="relative shrink-0 flex items-center gap-1">
          {templates.length > 0 && !alreadySent && (
            <div className="relative">
              <button
                data-testid={`hot-reply-template-btn-${reply.id}`}
                onClick={() => setShowTemplatePicker((v) => !v)}
                className="text-[11px] font-semibold px-2 py-1.5 rounded-md bg-white/[0.05] border border-white/10 hover:border-white/25 text-neutral-300 flex items-center gap-1"
                title="Apply a saved template"
              >
                <Files size={11} weight="fill" /> Template
              </button>
              {showTemplatePicker && (
                <div
                  data-testid={`template-picker-${reply.id}`}
                  className="absolute right-0 top-full mt-1 w-64 rounded-lg bg-[#141414] border border-white/10 shadow-lg z-20 py-1 max-h-64 overflow-y-auto scrollbar-thin"
                >
                  {templates.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => applyTemplate(t.id)}
                      data-testid={`template-option-${t.id}`}
                      className="w-full text-left px-3 py-2 hover:bg-white/[0.05] text-xs text-neutral-200"
                    >
                      <div className="font-semibold">{t.name}</div>
                      <div className="text-[10px] text-neutral-500 truncate">
                        {t.subject}
                      </div>
                      {t.use_count > 0 && (
                        <div className="text-[9px] text-neutral-600 mt-0.5">
                          Used {t.use_count}×
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <button
            data-testid={`hot-reply-draft-btn-${reply.id}`}
            onClick={toggleDraft}
            disabled={drafting || alreadySent}
            className="text-[11px] font-semibold px-2.5 py-1.5 rounded-md bg-[#FF5A1F]/15 border border-[#FF5A1F]/40 text-[#FF5A1F] hover:bg-[#FF5A1F]/25 disabled:opacity-50 flex items-center gap-1"
          >
            {drafting ? (
              <ArrowClockwise size={11} className="animate-spin" />
            ) : (
              <Sparkle size={11} weight="fill" />
            )}
            {alreadySent ? "Sent" : (showDraft ? "Hide" : "Draft reply")}
          </button>
        </div>
        <div className="text-[10px] text-neutral-500 shrink-0 tabular-nums">
          {receivedLabel}
        </div>
      </div>

      {/* Inline draft editor */}
      {showDraft && !alreadySent && (
        <div className="mt-2 ml-5 pl-4 border-l-2 border-[#FF5A1F]/30" data-testid={`hot-reply-draft-${reply.id}`}>
          <div className="text-[9px] uppercase tracking-widest text-[#FF5A1F] font-semibold mb-2">
            AI-drafted response · edit before sending
          </div>
          <input
            type="text"
            value={draftSubject}
            onChange={(e) => setDraftSubject(e.target.value)}
            placeholder="Subject"
            data-testid={`draft-subject-${reply.id}`}
            className="w-full mb-2 bg-white/[0.03] border border-white/10 rounded-md text-white text-[12px] py-1.5 px-2.5 focus:outline-none focus:border-[#FF5A1F]/60"
          />
          <textarea
            value={draftBody}
            onChange={(e) => { setDraftBody(e.target.value); setPreviewQuality(null); }}
            rows={4}
            placeholder={drafting ? "Drafting…" : "Body"}
            data-testid={`draft-body-${reply.id}`}
            className="w-full bg-white/[0.03] border border-white/10 rounded-md text-white text-[12px] py-2 px-2.5 focus:outline-none focus:border-[#FF5A1F]/60 leading-relaxed font-mono"
          />
          <DraftScoreStreakWidget
            refreshKey={streakRefresh}
            testid={`draft-streak-${reply.id}`}
          />
          {previewQuality && (
            <DraftGradePreview
              quality={previewQuality}
              onDismiss={() => setPreviewQuality(null)}
              testid={`draft-grade-preview-${reply.id}`}
            />
          )}
          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center gap-3">
              <button
                data-testid={`draft-regen-${reply.id}`}
                onClick={generateDraft}
                disabled={drafting || sending}
                className="text-[11px] text-neutral-400 hover:text-white disabled:opacity-50 flex items-center gap-1"
              >
                <ArrowClockwise size={11} className={drafting ? "animate-spin" : ""} /> Regenerate
              </button>
              <button
                data-testid={`draft-grade-btn-${reply.id}`}
                onClick={gradePreview}
                disabled={previewing || sending || drafting || !draftBody.trim()}
                className="text-[11px] text-emerald-300 hover:text-emerald-200 disabled:opacity-50 flex items-center gap-1"
              >
                <ArrowClockwise size={11} className={previewing ? "animate-spin" : ""} />
                {previewing ? "Grading…" : "Grade my draft"}
              </button>
            </div>
            <button
              data-testid={`draft-send-${reply.id}`}
              onClick={sendDraft}
              disabled={sending || drafting || !draftBody.trim()}
              className="rounded-md px-3 py-1.5 text-[12px] font-semibold bg-[#FF5A1F] text-black hover:bg-[#FF7038] disabled:opacity-50 flex items-center gap-1"
            >
              {sending ? (
                <ArrowClockwise size={11} className="animate-spin" />
              ) : (
                <PaperPlaneTilt size={11} weight="fill" />
              )}
              {sending ? "Sending…" : "Send reply"}
            </button>
          </div>
        </div>
      )}
      <ReplyCoachingModal
        quality={coachingQuality}
        onClose={() => setCoachingQuality(null)}
        leadEmail={reply.lead_email}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// ScheduleStrip — 14-day calendar mini-strip showing queued sends per day.
// In-window (Tue/Wed/Thu) days show a real bar height + count; off-window
// (Fri-Mon) days render as inert placeholders so the user sees WHY nothing
// is scheduled there.
// ---------------------------------------------------------------------------
const _WEEKDAY_LABELS = ["M", "T", "W", "T", "F", "S", "S"];
function ScheduleStrip({ schedule }) {
  // schedule = [{date, weekday, pending, sent, in_window}, ...]
  const max = Math.max(1, ...schedule.map((d) => (d.pending || 0) + (d.sent || 0)));

  return (
    <div className="mt-4 rounded-lg border border-white/10 bg-white/[0.02] p-3" data-testid="warmup-schedule-strip">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500">
          Next 14 days · sends per day
        </div>
        <div className="flex items-center gap-3 text-[10px] text-neutral-500">
          <span className="flex items-center gap-1">
            <span className="inline-block w-2 h-2 rounded-sm bg-blue-400"></span> Pending
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block w-2 h-2 rounded-sm bg-emerald-400"></span> Sent
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block w-2 h-2 rounded-sm bg-neutral-600"></span> Off-window
          </span>
        </div>
      </div>
      <div className="flex items-end gap-1 h-16" data-testid="warmup-schedule-days">
        {schedule.map((d) => {
          const total = (d.pending || 0) + (d.sent || 0);
          const heightPct = total > 0 ? Math.max(15, Math.round((total / max) * 100)) : 0;
          const day = new Date(d.date + "T12:00:00");
          const dayNum = day.getUTCDate();
          const monthShort = day.toLocaleDateString("en-US", { month: "short", timeZone: "UTC" });
          const isToday = new Date().toISOString().slice(0, 10) === d.date;
          const label = _WEEKDAY_LABELS[d.weekday % 7];

          return (
            <div
              key={d.date}
              className={`flex-1 flex flex-col items-center gap-0.5 rounded-md px-0.5 py-1 ${
                isToday ? "bg-white/[0.06] ring-1 ring-white/20" : ""
              }`}
              data-testid={`warmup-day-${d.date}`}
              title={
                d.in_window
                  ? `${monthShort} ${dayNum} · ${d.pending} pending, ${d.sent} sent`
                  : `${monthShort} ${dayNum} · Off-window (Fri–Mon)`
              }
            >
              <div className="w-full h-8 flex items-end justify-center">
                {d.in_window && total > 0 ? (
                  <div
                    className="w-full rounded-sm relative flex flex-col-reverse overflow-hidden"
                    style={{ height: `${heightPct}%` }}
                  >
                    {d.sent > 0 && (
                      <div
                        className="w-full bg-emerald-400"
                        style={{
                          height: `${(d.sent / total) * 100}%`,
                          minHeight: 2,
                        }}
                      />
                    )}
                    {d.pending > 0 && (
                      <div
                        className="w-full bg-blue-400"
                        style={{
                          height: `${(d.pending / total) * 100}%`,
                          minHeight: 2,
                        }}
                      />
                    )}
                  </div>
                ) : d.in_window ? (
                  <div className="w-full h-[3px] bg-white/[0.08] rounded-sm" />
                ) : (
                  <div className="w-full h-[3px] bg-neutral-700/60 rounded-sm" />
                )}
              </div>
              <div className={`text-[9px] font-semibold ${d.in_window ? "text-neutral-300" : "text-neutral-600"}`}>
                {label}
              </div>
              <div className={`text-[9px] tabular-nums ${d.in_window ? "text-neutral-400" : "text-neutral-600"}`}>
                {dayNum}
              </div>
              {total > 0 && (
                <div className="text-[9px] font-semibold text-white tabular-nums leading-tight">
                  {total}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Wave 16 · Coaching modal that pops when a reply scores <50 so the VA
// sees the AI critique instantly and can adjust the next reply. Non-
// blocking — the reply has already sent when this appears. Just a
// coaching moment, not an error state.
function ReplyCoachingModal({ quality, onClose, leadEmail }) {
  if (!quality) return null;
  const score = quality.score;
  const rationale = quality.rationale || "";
  const tags = Array.isArray(quality.tags) ? quality.tags : [];
  return (
    <div
      data-testid="reply-coaching-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        aria-label="Close coaching modal"
        onClick={onClose}
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
      />
      <div className="relative w-full max-w-md rounded-2xl border border-red-500/30 bg-[#0A0A0A] p-6 sm:p-7 shadow-2xl shadow-red-500/10">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 h-8 w-8 inline-flex items-center justify-center rounded-full text-neutral-500 hover:text-white hover:bg-white/5"
        >
          ×
        </button>
        <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-red-500/10 border border-red-500/40 text-[10px] uppercase tracking-widest text-red-300 font-semibold mb-3">
          ⚠ Coaching Moment · Score {score}/100
        </div>
        <h3 className="font-display text-lg sm:text-xl font-semibold text-white leading-tight mb-2">
          Your reply to {leadEmail} could be stronger.
        </h3>
        <p className="text-sm text-neutral-300 leading-relaxed mb-4">
          {rationale || "The AI grader flagged this reply as below the quality bar. Review the tags below and try a warmer, more personalised opening next time."}
        </p>
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-5" data-testid="reply-coaching-modal-tags">
            {tags.map((t) => (
              <span
                key={t}
                className="text-[10px] uppercase tracking-widest bg-red-500/5 border border-red-500/30 rounded-full px-2 py-0.5 text-red-300"
              >
                {t.replace(/_/g, " ")}
              </span>
            ))}
          </div>
        )}
        <div className="p-3 rounded-lg bg-emerald-500/[0.04] border border-emerald-500/20 mb-4">
          <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-semibold mb-1">
            Next reply — aim for
          </div>
          <ul className="text-xs text-neutral-300 leading-relaxed space-y-1 pl-4 list-disc">
            <li>Open with their name + company by name</li>
            <li>Reference something specific from their inbound message</li>
            <li>End with ONE concrete next step (call slot / doc link)</li>
          </ul>
        </div>
        <button
          type="button"
          onClick={onClose}
          data-testid="reply-coaching-modal-ack"
          className="w-full rounded-full px-5 py-2.5 text-sm border border-white/15 hover:border-white/40 hover:bg-white/5 text-white transition-colors"
        >
          Got it — I'll do better next time
        </button>
      </div>
    </div>
  );
}

// Inline draft-grade preview card. Shows the score + rationale + tags
// right below the reply textarea so the VA can iterate on the copy
// BEFORE sending. Score color-banded like the coaching modal (green
// ≥70, amber ≥50, red <50) so a glance tells the whole story.
function DraftGradePreview({ quality, onDismiss, testid }) {
  const score = quality?.score;
  if (score == null) return null;
  const band = score >= 70 ? "emerald" : score >= 50 ? "amber" : "red";
  const bandClass = {
    emerald: "border-emerald-500/40 bg-emerald-500/[0.06] text-emerald-300",
    amber: "border-amber-500/40 bg-amber-500/[0.06] text-amber-300",
    red: "border-red-500/40 bg-red-500/[0.06] text-red-300",
  }[band];
  const rationale = quality?.rationale || "";
  const tags = Array.isArray(quality?.tags) ? quality.tags : [];
  return (
    <div
      data-testid={testid}
      className={`mt-2 rounded-md border p-2.5 ${bandClass}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest font-semibold flex items-center gap-1.5">
            AI Draft Grade · {score}/100
            {band === "emerald" && <span className="text-[9px] opacity-80">— send it</span>}
            {band === "amber" && <span className="text-[9px] opacity-80">— consider a revise</span>}
            {band === "red" && <span className="text-[9px] opacity-80">— rewrite before sending</span>}
          </div>
          {rationale && (
            <div className="text-[11px] text-neutral-300 mt-1 leading-relaxed">
              {rationale}
            </div>
          )}
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1.5">
              {tags.map((t) => (
                <span
                  key={t}
                  className="text-[9px] uppercase tracking-widest bg-white/5 border border-white/10 rounded-full px-1.5 py-0.5 text-neutral-400"
                >
                  {t.replace(/_/g, " ")}
                </span>
              ))}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss grade"
          className="text-neutral-500 hover:text-white text-lg leading-none shrink-0"
        >
          ×
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DraftScoreStreakWidget — mini "78 → 82 → 88 ↑" sparkline showing the
// VA's recent draft-grade history so they can see improvement in real
// time as they iterate on the current draft. Hydrates from
// `/admin/leads/draft-grade-history` (per-VA, oldest→newest). Rerenders
// whenever `refreshKey` changes (bumped after every successful grade).
// ---------------------------------------------------------------------------
function DraftScoreStreakWidget({ refreshKey, testid }) {
  const [items, setItems] = useState([]);
  const [trend, setTrend] = useState(null);
  const [latest, setLatest] = useState(null);
  const [loaded, setLoaded] = useState(false);
  // Wave 16 · Draft Grade Nudge — throttle so we don't spam a VA
  // with the same "try a warmer opener" toast on every low-score
  // reply. Fire at most once per 5-min window per composer.
  const lastNudgeAt = useRef(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await client.get("/admin/leads/draft-grade-history", {
          params: { limit: 8 },
        });
        if (cancelled) return;
        setItems(Array.isArray(data?.items) ? data.items : []);
        setTrend(data?.trend || null);
        setLatest(typeof data?.latest === "number" ? data.latest : null);
        // Fire the nudge ONLY when the composer actually just graded
        // a new draft (refreshKey > 0), not on initial page load,
        // and no sooner than 5 min since the last nudge.
        if (
          data?.nudge_needed &&
          data?.nudge_message &&
          refreshKey > 0 &&
          Date.now() - lastNudgeAt.current > 5 * 60 * 1000
        ) {
          lastNudgeAt.current = Date.now();
          toast(data.nudge_message, {
            icon: "🎯",
            duration: 6000,
            className: "draft-grade-nudge-toast",
            id: `draft-nudge-${refreshKey}`,  // dedupe across refetches
          });
        }
      } catch (_e) {
        // Silent — this is a delight-only widget; never blocks composing.
        if (!cancelled) {
          setItems([]);
          setTrend(null);
          setLatest(null);
        }
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [refreshKey]);

  if (!loaded || items.length === 0) return null;

  const scores = items.map((i) => i.score);
  const minS = Math.min(...scores);
  const maxS = Math.max(...scores);
  const range = Math.max(1, maxS - minS);
  const w = 84;   // sparkline width (px)
  const h = 20;   // sparkline height (px)
  const step = scores.length > 1 ? w / (scores.length - 1) : 0;
  const points = scores.map((s, idx) => {
    const x = scores.length === 1 ? w / 2 : idx * step;
    // Invert Y (SVG origin is top-left) — higher scores = higher on chart
    const y = h - ((s - minS) / range) * h;
    return { x, y, s };
  });
  const path = points.map((p, idx) => `${idx === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");

  const trendArrow = trend === "up" ? "↑" : trend === "down" ? "↓" : "→";
  const trendClass =
    trend === "up" ? "text-emerald-300"
    : trend === "down" ? "text-red-300"
    : "text-neutral-400";
  const latestBand =
    latest == null ? "text-neutral-400"
    : latest >= 70 ? "text-emerald-300"
    : latest >= 50 ? "text-amber-300"
    : "text-red-300";

  // Compact human sequence: "78 → 82 → 88" (last 3 shown to keep it short)
  const sequenceStr = scores.slice(-3).join(" → ");

  return (
    <div
      data-testid={testid}
      className="mt-2 rounded-md border border-white/10 bg-white/[0.03] px-2.5 py-2 flex items-center gap-3"
      title="Your recent draft grades — trend arrow compares first vs latest"
    >
      <div className="text-[9px] uppercase tracking-widest text-neutral-500 font-semibold shrink-0">
        Session streak
      </div>
      <svg
        width={w}
        height={h}
        viewBox={`0 0 ${w} ${h}`}
        className="shrink-0"
        aria-hidden="true"
        data-testid={testid ? `${testid}-spark` : undefined}
      >
        <path
          d={path}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          className={trendClass}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {points.map((p, idx) => {
          const dotColor =
            p.s >= 70 ? "#34d399"
            : p.s >= 50 ? "#fbbf24"
            : "#f87171";
          const isLast = idx === points.length - 1;
          return (
            <circle
              key={idx}
              cx={p.x}
              cy={p.y}
              r={isLast ? 2.4 : 1.6}
              fill={dotColor}
            />
          );
        })}
      </svg>
      <div className="text-[11px] text-neutral-300 tabular-nums flex-1 min-w-0 truncate">
        {sequenceStr}{" "}
        <span className={`font-semibold ${trendClass}`}>{trendArrow}</span>
      </div>
      {latest != null && (
        <div
          className={`text-[11px] font-bold tabular-nums shrink-0 ${latestBand}`}
          data-testid={testid ? `${testid}-latest` : undefined}
        >
          {latest}
        </div>
      )}
    </div>
  );
}

function ComposeModal({ open, lead, onClose, onSent, adminEmail }) {
  const [tone, setTone] = useState("warm");
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState(null);
  const [replyTo, setReplyTo] = useState(DEFAULT_REPLY_TO);
  const [gmailBrokenBanner, setGmailBrokenBanner] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  useEffect(() => {
    if (open) {
      setDraft(null);
      setTone("warm");
      setReplyTo(DEFAULT_REPLY_TO);
      setGmailBrokenBanner(false);
    }
  }, [open, adminEmail]);

  if (!open || !lead) return null;

  const generate = async () => {
    setDrafting(true);
    try {
      const { data } = await client.post("/admin/leads/draft", { lead_id: lead.id, tone });
      setDraft(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI drafting failed");
    } finally {
      setDrafting(false);
    }
  };

  const disconnectBrokenGmail = async () => {
    setDisconnecting(true);
    try {
      await client.delete("/settings/gmail-smtp");
      toast.success("Broken Gmail disconnected. Retrying send…");
      setGmailBrokenBanner(false);
      await send();  // auto-retry the send
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't disconnect Gmail");
    } finally {
      setDisconnecting(false);
    }
  };

  const send = async () => {
    if (!draft?.subject || !draft?.body) {
      toast.error("Generate a draft first");
      return;
    }
    setSending(true);
    try {
      const { data } = await client.post("/admin/leads/send", {
        lead_id: lead.id,
        subject: draft.subject,
        body: draft.body,
        reply_to: replyTo || undefined,
      });
      if (data?.status === "sent") {
        const via = data.provider === "gmail_smtp" ? " (via your Gmail)" : "";
        toast.success(`✅ Sent to ${lead.email}${via}`);
        if (data.gmail_auto_disabled) {
          toast.warning("Gmail was auto-disabled (app password broken). Reconnect at Settings → Pricing.", { duration: 6000 });
        }
        onSent?.();
        onClose();
      } else if (data?.need_gmail_reconnect) {
        setGmailBrokenBanner(true);
        toast.error(data?.error || "Gmail app password broken", { duration: 6000 });
      } else {
        toast.error(data?.error || "Send failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8" data-testid="lead-compose-modal">
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-[#0F0F0F] border border-white/10 rounded-2xl w-full max-w-2xl shadow-2xl">
        <div className="flex items-start justify-between p-6 border-b border-white/10">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#FF5A1F] font-semibold mb-1">Cold Outreach</div>
            <h2 className="text-xl font-display font-semibold text-white">{lead.dba_name || lead.legal_name}</h2>
            <p className="text-xs text-neutral-500 mt-1">
              {lead.email} · {lead.power_units || "?"} trucks · {lead.phy_city}, {lead.phy_state}
            </p>
          </div>
          <button onClick={onClose} data-testid="lead-modal-close" className="text-neutral-500 hover:text-white shrink-0">
            <XIcon size={20} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {gmailBrokenBanner && (
            <div
              className="rounded-xl border border-red-500/40 bg-red-500/[0.08] p-4 flex items-start gap-3"
              data-testid="gmail-broken-banner"
            >
              <Warning size={20} weight="fill" className="text-red-400 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-red-300 text-sm font-semibold mb-1">
                  Gmail app password is broken
                </div>
                <p className="text-xs text-neutral-400 leading-relaxed">
                  Google rejected your saved 16-char app password. Disconnect it and this send
                  will retry through the shared FreightPilot inbox (Resend) — no downtime.
                  You can reconnect a fresh Gmail later at Settings → Pricing.
                </p>
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={disconnectBrokenGmail}
                    disabled={disconnecting || sending}
                    data-testid="disconnect-broken-gmail-btn"
                    className="rounded-full py-2 px-4 text-xs font-semibold bg-red-500 text-white hover:bg-red-400 disabled:opacity-50 transition-colors"
                  >
                    {disconnecting ? "Disconnecting…" : "Disconnect Gmail & retry send"}
                  </button>
                  <button
                    onClick={() => setGmailBrokenBanner(false)}
                    className="rounded-full py-2 px-4 text-xs font-semibold border border-white/15 text-neutral-300 hover:bg-white/5 transition-colors"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          )}

          {!draft ? (
            <>
              <div>
                <label className="block text-xs uppercase tracking-widest text-neutral-500 mb-3">Tone</label>
                <div className="grid grid-cols-3 gap-2">
                  {TONE_OPTIONS.map((t) => (
                    <button
                      key={t.value}
                      onClick={() => setTone(t.value)}
                      data-testid={`lead-tone-${t.value}`}
                      className={`text-left p-3 rounded-lg border transition-all ${
                        tone === t.value
                          ? "border-[#FF5A1F]/60 bg-[#FF5A1F]/[0.08] ring-1 ring-[#FF5A1F]/30"
                          : "border-white/10 hover:border-white/25"
                      }`}
                    >
                      <div className="text-white text-sm font-semibold">{t.label}</div>
                      <div className="text-[11px] text-neutral-400 mt-0.5 leading-snug">{t.desc}</div>
                    </button>
                  ))}
                </div>
              </div>
              <button
                onClick={generate}
                disabled={drafting}
                data-testid="lead-generate-btn"
                className="w-full rounded-full py-3 text-sm font-semibold bg-[#007AFF] text-white hover:bg-[#3392FF] disabled:opacity-50 transition-colors inline-flex items-center justify-center gap-2"
              >
                <Sparkle size={14} weight="fill" />
                {drafting ? "AI drafting…" : "Generate personalized cold email"}
              </button>
            </>
          ) : (
            <>
              <div>
                <label className="block text-xs uppercase tracking-widest text-neutral-500 mb-2">Subject</label>
                <input
                  type="text"
                  value={draft.subject}
                  onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
                  data-testid="lead-subject-input"
                  className="w-full bg-white/[0.03] border border-white/10 rounded-lg text-white text-sm py-2.5 px-3 focus:outline-none focus:border-[#FF5A1F]/60"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-neutral-500 mb-2">Body (edit before sending)</label>
                <textarea
                  value={draft.body}
                  onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                  rows={10}
                  data-testid="lead-body-textarea"
                  className="w-full bg-white/[0.03] border border-white/10 rounded-lg text-white text-sm py-2.5 px-3 focus:outline-none focus:border-[#FF5A1F]/60 font-mono leading-relaxed"
                />
                <p className="text-[11px] text-neutral-500 mt-1.5">
                  A "See FreightPilot AI →" CTA auto-appends with UTM tracking (utm_campaign=cold_outreach).
                </p>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-neutral-500 mb-2">Reply-To</label>
                <input
                  type="email"
                  value={replyTo}
                  onChange={(e) => setReplyTo(e.target.value)}
                  data-testid="lead-replyto-input"
                  className="w-full bg-white/[0.03] border border-white/10 rounded-lg text-white text-sm py-2.5 px-3 focus:outline-none focus:border-[#FF5A1F]/60"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => setDraft(null)}
                  data-testid="lead-back-btn"
                  className="rounded-full px-4 py-2.5 text-sm font-medium border border-white/10 text-neutral-400 hover:text-white hover:border-white/25 transition-colors inline-flex items-center gap-1.5"
                >
                  <ArrowUUpLeft size={13} /> Regenerate
                </button>
                <button
                  onClick={send}
                  disabled={sending}
                  data-testid="lead-send-btn"
                  className="flex-1 rounded-full py-2.5 text-sm font-semibold bg-[#FF5A1F] text-black hover:bg-[#FF7038] disabled:opacity-50 transition-colors inline-flex items-center justify-center gap-2"
                >
                  <PaperPlaneTilt size={13} weight="fill" />
                  {sending ? "Sending…" : `Send to ${lead.email}`}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function FetchPanel({ onFetched }) {
  const [state, setState] = useState("TX");
  const [minUnits, setMinUnits] = useState(3);
  const [maxUnits, setMaxUnits] = useState(25);
  const [limit, setLimit] = useState(50);
  const [fetching, setFetching] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const fetchLeads = async () => {
    if (!state.trim()) {
      toast.error("Enter a state code (e.g., TX)");
      return;
    }
    setFetching(true);
    try {
      const { data } = await client.post("/admin/leads/fetch-fmcsa", {
        state: state.trim().toUpperCase(),
        min_units: Number(minUnits) || 3,
        max_units: Number(maxUnits) || 25,
        require_email: true,
        limit: Number(limit) || 50,
      });
      setLastResult(data);
      if (data.imported > 0) {
        toast.success(`✅ Fetched ${data.imported} new leads from FMCSA (${data.duplicates_skipped} dupes, ${data.filtered_by_fleet_size} outside size range)`);
      } else {
        toast(`No new leads (all ${data.duplicates_skipped} were duplicates or ${data.filtered_by_fleet_size} were outside size range). Try widening the fleet size range or picking a different state.`);
      }
      onFetched?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "FMCSA fetch failed");
    } finally {
      setFetching(false);
    }
  };

  return (
    <div className="card-surface p-6" data-testid="fmcsa-fetch-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-neutral-500 mb-2">
            <Cloud size={13} weight="fill" className="text-[#007AFF]" /> One-Click Fetch — FMCSA Live API
          </div>
          <p className="text-sm text-neutral-400 leading-relaxed max-w-2xl">
            Pull fresh trucker leads directly from the FMCSA public database. No CSV download, no Excel filtering — just pick your criteria and go. Newest registrations first.
          </p>
        </div>
        <div className="inline-flex items-center gap-1.5 text-[10px] text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-2 py-1 font-semibold">
          <Lightning size={11} weight="fill" /> INSTANT
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1 block">State</label>
          <input
            type="text"
            maxLength={2}
            value={state}
            onChange={(e) => setState(e.target.value.toUpperCase())}
            placeholder="TX"
            data-testid="fmcsa-fetch-state"
            className="w-full bg-white/[0.03] border border-white/10 rounded-lg text-white text-sm py-2 px-3 uppercase focus:outline-none focus:border-[#007AFF]/60"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1 block">Min Trucks</label>
          <input
            type="number"
            min={1}
            value={minUnits}
            onChange={(e) => setMinUnits(e.target.value)}
            data-testid="fmcsa-fetch-min"
            className="w-full bg-white/[0.03] border border-white/10 rounded-lg text-white text-sm py-2 px-3 focus:outline-none focus:border-[#007AFF]/60"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1 block">Max Trucks</label>
          <input
            type="number"
            min={1}
            value={maxUnits}
            onChange={(e) => setMaxUnits(e.target.value)}
            data-testid="fmcsa-fetch-max"
            className="w-full bg-white/[0.03] border border-white/10 rounded-lg text-white text-sm py-2 px-3 focus:outline-none focus:border-[#007AFF]/60"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1 block">Limit</label>
          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            data-testid="fmcsa-fetch-limit"
            className="w-full bg-white/[0.03] border border-white/10 rounded-lg text-white text-sm py-2 px-3 focus:outline-none focus:border-[#007AFF]/60"
          >
            <option value={10}>10 leads</option>
            <option value={25}>25 leads</option>
            <option value={50}>50 leads</option>
            <option value={100}>100 leads</option>
            <option value={250}>250 leads</option>
          </select>
        </div>
        <div className="flex items-end">
          <button
            onClick={fetchLeads}
            disabled={fetching}
            data-testid="fmcsa-fetch-btn"
            className="w-full rounded-lg py-2 text-sm font-semibold bg-[#007AFF] text-white hover:bg-[#3392FF] disabled:opacity-50 transition-colors inline-flex items-center justify-center gap-2"
          >
            <Cloud size={14} weight="fill" />
            {fetching ? "Fetching…" : "Fetch Leads"}
          </button>
        </div>
      </div>
      {lastResult && (
        <div className="mt-4 pt-4 border-t border-white/10 text-xs text-neutral-400 flex flex-wrap gap-x-6 gap-y-1" data-testid="fmcsa-fetch-result">
          <span>Imported: <b className="text-emerald-400">{lastResult.imported}</b></span>
          <span>Duplicates: <b className="text-neutral-300">{lastResult.duplicates_skipped}</b></span>
          <span>Outside size range: <b className="text-neutral-300">{lastResult.filtered_by_fleet_size}</b></span>
          <span>No email: <b className="text-neutral-300">{lastResult.no_email_skipped}</b></span>
          <span>Total from FMCSA: <b className="text-neutral-300">{lastResult.total_fetched}</b></span>
        </div>
      )}
      <div className="mt-3 text-[11px] text-neutral-500 leading-relaxed">
        💡 Tip: For best results, target one state at a time. Try 3-15 trucks (small fleets ripe for automation). Only carriers with emails on file are returned.
      </div>
    </div>
  );
}

function ImportPanel({ onImported }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const upload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().match(/\.(csv|txt)$/)) {
      toast.error("Please upload a .csv file");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("max_rows", "2000");
      const { data } = await client.post("/admin/leads/import-fmcsa", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLastResult(data);
      toast.success(`✅ Imported ${data.imported} leads (${data.duplicates_skipped} dupes, ${data.no_email_skipped} without email)`);
      onImported?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="card-surface p-6" data-testid="fmcsa-import-panel">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-neutral-500 mb-3">
        <FileCsv size={13} /> Or Upload Your Own CSV
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <p className="text-sm text-neutral-400 leading-relaxed">
            Upload the free FMCSA public carrier CSV. We auto-parse DOT#, company name, address, phone, email, fleet size + mileage. Rows without an email are skipped.
          </p>
          <a
            href="https://ai.fmcsa.dot.gov/SMS/Tools/Downloads.aspx"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="fmcsa-download-link"
            className="mt-3 inline-flex items-center gap-1.5 text-xs text-[#007AFF] hover:underline"
          >
            Download the FMCSA CSV →
          </a>
        </div>
        <div>
          <label
            className={`flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed p-6 cursor-pointer transition-colors ${
              uploading ? "opacity-50 pointer-events-none" : "border-white/15 hover:border-[#FF5A1F]/50 hover:bg-[#FF5A1F]/[0.03]"
            }`}
          >
            <UploadSimple size={24} className="text-neutral-400" />
            <div className="text-sm text-white font-semibold">
              {uploading ? "Importing…" : "Drop CSV here or click to browse"}
            </div>
            <div className="text-[11px] text-neutral-500">Up to 40 MB · Max 2000 rows per import</div>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => upload(e.target.files?.[0])}
              className="hidden"
              data-testid="fmcsa-file-input"
            />
          </label>
        </div>
      </div>
      {lastResult && (
        <div className="mt-4 pt-4 border-t border-white/10 text-xs text-neutral-400 flex flex-wrap gap-x-6 gap-y-1" data-testid="fmcsa-import-result">
          <span>Imported: <b className="text-emerald-400">{lastResult.imported}</b></span>
          <span>Duplicates skipped: <b className="text-neutral-300">{lastResult.duplicates_skipped}</b></span>
          <span>No email: <b className="text-neutral-300">{lastResult.no_email_skipped}</b></span>
          <span>Rows processed: <b className="text-neutral-300">{lastResult.processed_rows}</b></span>
        </div>
      )}
    </div>
  );
}

export default function AdminLeadsPage() {
  const { user } = useAuth();
  const wizardRef = useRef(null);
  const [stats, setStats] = useState(null);
  const [outreachStats, setOutreachStats] = useState(null);
  const [leadsData, setLeadsData] = useState({ leads: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBatchReview, setShowBatchReview] = useState(false);
  const [batchDrafts, setBatchDrafts] = useState(null);
  const [drafting, setDrafting] = useState(false);
  const [filters, setFilters] = useState({ state: "", min_units: "", max_units: "", contacted: "no" });
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState("");
  const limit = 25;

  const reloadStats = async () => {
    try {
      const { data } = await client.get("/admin/leads/stats");
      setStats(data);
    } catch (e) { /* silent */ }
    try {
      const { data } = await client.get("/admin/leads/outreach-stats");
      setOutreachStats(data);
    } catch (e) { /* silent */ }
  };

  const reloadLeads = async (nextOffset = offset) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.state) params.set("state", filters.state);
      if (filters.min_units) params.set("min_units", filters.min_units);
      if (filters.max_units) params.set("max_units", filters.max_units);
      if (filters.contacted && filters.contacted !== "any") params.set("contacted", filters.contacted);
      params.set("limit", String(limit));
      params.set("offset", String(nextOffset));
      const { data } = await client.get(`/admin/leads?${params.toString()}`);
      setLeadsData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reloadStats(); reloadLeads(0); setOffset(0); /* eslint-disable-next-line */ }, [filters]);

  const handleDelete = async (leadId, company) => {
    if (!window.confirm(`Delete lead "${company}"? This can't be undone.`)) return;
    try {
      await client.delete(`/admin/leads/${leadId}`);
      toast.success("Lead deleted");
      reloadLeads();
      reloadStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const toggleSelect = (leadId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(leadId)) next.delete(leadId); else next.add(leadId);
      return next;
    });
  };

  const selectAllUncontacted = () => {
    const eligible = filteredLeads.filter((l) => !l.contacted).slice(0, 20);
    setSelectedIds(new Set(eligible.map((l) => l.id)));
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleBatchDraft = async () => {
    if (selectedIds.size === 0) return;
    if (selectedIds.size > 20) {
      toast.error("Batch limit is 20 leads at a time. Deselect a few.");
      return;
    }
    setDrafting(true);
    try {
      const { data } = await client.post("/admin/leads/batch-draft", {
        lead_ids: Array.from(selectedIds),
        tone: "warm",
      });
      setBatchDrafts(data.drafts);
      setShowBatchReview(true);
      toast.success(`Drafted ${data.ok_count} of ${data.total}. Review + send below.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Batch draft failed");
    } finally {
      setDrafting(false);
    }
  };

  const filteredLeads = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return leadsData.leads;
    return leadsData.leads.filter((l) =>
      (l.legal_name || "").toLowerCase().includes(s) ||
      (l.dba_name || "").toLowerCase().includes(s) ||
      (l.email || "").toLowerCase().includes(s) ||
      (l.phy_city || "").toLowerCase().includes(s)
    );
  }, [leadsData.leads, search]);

  if (!user?.is_admin) {
    return (
      <div className="card-surface p-8 text-center" data-testid="admin-leads-forbidden">
        <Warning size={40} weight="fill" className="text-red-400 mx-auto mb-3" />
        <h1 className="text-xl font-semibold text-white mb-2">Admin only</h1>
        <p className="text-neutral-400 text-sm">This tool is restricted to admin accounts.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8" data-testid="admin-leads-page">
      <FirstColdEmailWizard ref={wizardRef} onComplete={() => { reloadStats(); reloadLeads(); }} />
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-[#FF5A1F]/30 bg-[#FF5A1F]/10 text-[#FF5A1F] px-3 py-1 text-xs font-semibold mb-3">
            <Truck size={12} weight="fill" /> COLD OUTREACH · FMCSA LEADS
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold text-white tracking-tight">
            Find truckers, close pilots
          </h1>
          <p className="text-neutral-400 mt-2 text-sm sm:text-base max-w-2xl">
            Import free FMCSA carrier data, filter by fleet size + state, and send AI-personalized cold emails from your Gmail. Zero-cost lead gen.
          </p>
        </div>
        {/* Wave 13: Replay setup — force-opens the wizard */}
        <button
          onClick={() => wizardRef.current?.replay()}
          data-testid="wizard-replay-btn"
          className="rounded-full px-3 py-1.5 text-[11px] border border-white/10 bg-white/[0.03] text-neutral-400 hover:border-[#FF5A1F]/40 hover:text-[#FF9764] transition-colors inline-flex items-center gap-1.5 shrink-0 self-start"
          title="Re-run the guided setup wizard"
        >
          🔁 Replay setup
        </button>
      </div>

      {/* Hot Replies — the highest-signal thing on this page. If any lead
          just replied to a cold email, it appears here at the top with a
          pulsing red badge. Everything else on this page is secondary. */}
      <HotRepliesStrip
        onLeadClick={(leadId) => {
          // Jump the lead into focus by matching + selecting them
          const target = leadsData.leads.find((l) => l.id === leadId);
          if (target) {
            setSelected(target);
          } else {
            toast.info("Lead may be on another page — try clearing filters");
          }
        }}
      />

      {/* Demo calendar — upcoming booked demos + past ones awaiting result */}
      <DemoStrip />

      {/* Deliverability Doctor — the "is my domain broken?" tool. Sits at
          the top so it's the first thing seen. Fixing SPF/DKIM/DMARC here
          is what actually changes 0% opens → 40%+ opens. */}
      <DeliverabilityDoctor adminEmail={user?.email} />

      {/* Safe Warmup Sender — paced drip alternative to batch-blasting.
          The safe way to build sender rep after a broken-DNS incident. */}
      <WarmupPanel
        selectedIds={selectedIds}
        onScheduled={() => { reloadStats(); reloadLeads(); clearSelection(); }}
      />

      <FetchPanel onFetched={() => { reloadStats(); reloadLeads(0); }} />
      <ImportPanel onImported={() => { reloadStats(); reloadLeads(0); }} />

      {/* Outreach performance stats — reply rate, follow-ups, weekly volume */}
      {outreachStats && (
        <OutreachPerformanceCard stats={outreachStats} />
      )}

      {/* Win story — one-line proof woven into AI follow-up emails */}
      <WinStoryEditor />

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card-surface p-5" data-testid="leads-stat-total">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Total Leads</div>
          <div className="text-3xl font-display font-semibold text-white">{stats?.total ?? 0}</div>
        </div>
        <div className="card-surface p-5" data-testid="leads-stat-uncontacted">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Uncontacted</div>
          <div className="text-3xl font-display font-semibold text-[#FF5A1F]">{stats?.uncontacted ?? 0}</div>
        </div>
        <div className="card-surface p-5" data-testid="leads-stat-contacted">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Contacted</div>
          <div className="text-3xl font-display font-semibold text-emerald-400">{stats?.contacted ?? 0}</div>
        </div>
        <div className="card-surface p-5" data-testid="leads-stat-states">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Top States</div>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {(stats?.by_state || []).slice(0, 5).map((s) => (
              <span key={s.state} className="text-[11px] px-2 py-0.5 rounded-full bg-white/[0.05] text-neutral-300 border border-white/10">
                {s.state} · {s.count}
              </span>
            ))}
            {(!stats?.by_state || stats.by_state.length === 0) && (
              <span className="text-xs text-neutral-500">—</span>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card-surface p-4" data-testid="leads-filters">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1 block">State</label>
            <input
              type="text"
              placeholder="e.g. TX"
              maxLength={2}
              value={filters.state}
              onChange={(e) => setFilters({ ...filters, state: e.target.value.toUpperCase() })}
              data-testid="leads-filter-state"
              className="w-full bg-white/[0.03] border border-white/10 rounded-lg text-white text-sm py-2 px-3 uppercase focus:outline-none focus:border-[#FF5A1F]/60"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1 block">Min Trucks</label>
            <input
              type="number"
              placeholder="3"
              value={filters.min_units}
              onChange={(e) => setFilters({ ...filters, min_units: e.target.value })}
              data-testid="leads-filter-min-units"
              className="w-full bg-white/[0.03] border border-white/10 rounded-lg text-white text-sm py-2 px-3 focus:outline-none focus:border-[#FF5A1F]/60"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1 block">Max Trucks</label>
            <input
              type="number"
              placeholder="25"
              value={filters.max_units}
              onChange={(e) => setFilters({ ...filters, max_units: e.target.value })}
              data-testid="leads-filter-max-units"
              className="w-full bg-white/[0.03] border border-white/10 rounded-lg text-white text-sm py-2 px-3 focus:outline-none focus:border-[#FF5A1F]/60"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1 block">Status</label>
            <select
              value={filters.contacted}
              onChange={(e) => setFilters({ ...filters, contacted: e.target.value })}
              data-testid="leads-filter-status"
              className="w-full bg-white/[0.03] border border-white/10 rounded-lg text-white text-sm py-2 px-3 focus:outline-none focus:border-[#FF5A1F]/60"
            >
              <option value="no">Uncontacted only</option>
              <option value="yes">Contacted only</option>
              <option value="any">All</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1 block">Search</label>
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
              <input
                type="text"
                placeholder="Company, email…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="leads-search"
                className="w-full bg-white/[0.03] border border-white/10 rounded-lg text-white text-sm py-2 pl-9 pr-3 focus:outline-none focus:border-[#FF5A1F]/60"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Leads table */}
      <div className="card-surface" data-testid="leads-list">
        {loading ? (
          <div className="text-neutral-500 text-sm py-12 text-center">Loading…</div>
        ) : filteredLeads.length === 0 ? (
          <div className="text-neutral-500 text-sm py-12 text-center border border-dashed border-white/10 rounded-lg m-4">
            {stats?.total === 0 ? (
              <>
                No leads yet. Import an FMCSA CSV above to get started.
                <div className="text-[11px] text-neutral-600 mt-2">
                  You can also test with a sample file — I've seeded 4 dummy leads for you.
                </div>
              </>
            ) : (
              <>No leads match your filters. Try widening the criteria.</>
            )}
          </div>
        ) : (
          <div className="divide-y divide-white/[0.05]">
            {filteredLeads.map((l) => {
              const isSelected = selectedIds.has(l.id);
              const canSelect = !l.contacted;
              return (
              <div
                key={l.id}
                data-testid={`lead-row-${l.email}`}
                className={`flex flex-col sm:flex-row sm:items-center gap-3 p-3 sm:p-4 transition-colors ${isSelected ? "bg-[#FF5A1F]/[0.06]" : ""}`}
              >
                {canSelect && (
                  <label className="flex items-center shrink-0 cursor-pointer select-none" title="Select for batch outreach">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSelect(l.id)}
                      data-testid={`lead-select-${l.email}`}
                      className="w-4 h-4 accent-[#FF5A1F] cursor-pointer"
                    />
                  </label>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-white font-medium text-sm truncate">
                    {l.dba_name || l.legal_name}
                    {l.dba_name && l.legal_name !== l.dba_name && (
                      <span className="text-neutral-500 font-normal ml-1.5 text-xs">({l.legal_name})</span>
                    )}
                  </div>
                  <div className="text-xs text-neutral-500 truncate flex items-center gap-2 mt-0.5">
                    <Envelope size={11} />{l.email}
                    {l.phone && <> · {l.phone}</>}
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0 flex-wrap">
                  <div className="flex items-center gap-1.5 text-[11px] text-neutral-400 bg-white/[0.03] px-2 py-1 rounded-full border border-white/10">
                    <Truck size={11} /> {l.power_units || "?"}
                  </div>
                  <div className="flex items-center gap-1.5 text-[11px] text-neutral-400 bg-white/[0.03] px-2 py-1 rounded-full border border-white/10">
                    <MapPin size={11} /> {l.phy_city || "?"}, {l.phy_state || "?"}
                  </div>
                  {l.interested && (
                    <span className="text-[10px] px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 font-semibold" data-testid={`lead-interested-${l.email}`} title="AI-classified interested reply — fast-laned to the top of the list">
                      🔥 INTERESTED
                    </span>
                  )}
                  {l.hot_opener && !l.replied && (
                    <span className="text-[10px] px-2 py-1 rounded-full bg-orange-500/15 text-orange-400 border border-orange-500/30 font-semibold" data-testid={`lead-hot-opener-${l.email}`} title="Opened 2+ times, never replied — follow-up auto-prioritized">
                      🔥 HOT OPENER
                    </span>
                  )}
                  {(l.hot_opener || l.last_call_outcome) && !l.replied && (
                    <CallOutcomeSelect lead={l} />
                  )}
                  {l.contacted ? (
                    <span className="text-[10px] px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 font-semibold">
                      CONTACTED
                    </span>
                  ) : (
                    <button
                      onClick={() => setSelected(l)}
                      data-testid={`lead-compose-btn-${l.email}`}
                      className="rounded-full px-3 py-1.5 text-xs font-semibold bg-[#FF5A1F] text-black hover:bg-[#FF7038] transition-colors inline-flex items-center gap-1.5"
                    >
                      <ChatCircleText size={11} weight="fill" /> Draft
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(l.id, l.legal_name)}
                    data-testid={`lead-delete-btn-${l.email}`}
                    className="text-neutral-500 hover:text-red-400 transition-colors p-1"
                    title="Delete lead"
                  >
                    <Trash size={14} />
                  </button>
                </div>
              </div>
              );
            })}
          </div>
        )}
        {/* Pagination */}
        {leadsData.total > limit && (
          <div className="flex items-center justify-between p-4 border-t border-white/10 text-xs text-neutral-400">
            <div>
              Showing {offset + 1}–{Math.min(offset + limit, leadsData.total)} of {leadsData.total}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => { const o = Math.max(0, offset - limit); setOffset(o); reloadLeads(o); }}
                disabled={offset === 0}
                data-testid="leads-pagination-prev"
                className="rounded-full px-3 py-1.5 border border-white/10 hover:border-white/25 disabled:opacity-30 transition-colors"
              >
                Prev
              </button>
              <button
                onClick={() => { const o = offset + limit; setOffset(o); reloadLeads(o); }}
                disabled={offset + limit >= leadsData.total}
                data-testid="leads-pagination-next"
                className="rounded-full px-3 py-1.5 border border-white/10 hover:border-white/25 disabled:opacity-30 transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      <ComposeModal
        open={!!selected}
        lead={selected}
        onClose={() => setSelected(null)}
        onSent={() => { reloadStats(); reloadLeads(); }}
        adminEmail={user?.email}
      />

      {/* Batch selection sticky bar */}
      {selectedIds.size > 0 && (
        <div
          data-testid="batch-outreach-bar"
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 rounded-2xl bg-[#0A0A0A] border border-[#FF5A1F]/30 shadow-2xl shadow-[#FF5A1F]/10 px-5 py-3 flex items-center gap-4 backdrop-blur-lg"
        >
          <div className="text-sm">
            <span className="text-white font-semibold">{selectedIds.size}</span>
            <span className="text-neutral-400"> selected · </span>
            <span className="text-neutral-500 text-xs">max 20 per batch</span>
          </div>
          <button
            onClick={clearSelection}
            data-testid="batch-clear-btn"
            className="text-neutral-500 hover:text-white transition-colors text-xs px-2"
          >
            Clear
          </button>
          <button
            onClick={handleBatchDraft}
            disabled={drafting || selectedIds.size > 20}
            data-testid="batch-draft-btn"
            className="rounded-full px-5 py-2 text-sm font-semibold bg-[#FF5A1F] text-black hover:bg-[#FF7038] disabled:opacity-60 transition-colors inline-flex items-center gap-2"
          >
            {drafting ? "Drafting…" : <>Draft {selectedIds.size} emails <ChatCircleText size={13} weight="fill" /></>}
          </button>
        </div>
      )}

      {/* Batch review modal — user reviews drafted emails, sends all approved */}
      {showBatchReview && batchDrafts && (
        <BatchReviewModal
          drafts={batchDrafts}
          replyTo="contact@freightpilotai.ai"
          onClose={() => { setShowBatchReview(false); setBatchDrafts(null); }}
          onSent={() => {
            setShowBatchReview(false);
            setBatchDrafts(null);
            setSelectedIds(new Set());
            reloadStats();
            reloadLeads();
          }}
        />
      )}
    </div>
  );
}

// ==================== OUTREACH PERFORMANCE CARD ====================
function OutreachPerformanceCard({ stats }) {
  return (
    <div className="card-surface p-5" data-testid="outreach-stats-card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">OUTREACH PERFORMANCE</div>
          <h2 className="font-display text-lg font-semibold text-white">How your cold outreach is doing</h2>
        </div>
        <div className="text-[11px] text-neutral-500">
          Last 7 days: <span className="text-white font-semibold">{stats.emails_sent_last_7d}</span> sent
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 text-center">
        <StatCell label="Total sent" value={stats.emails_sent_total} />
        <StatCell label="Contacted" value={stats.contacted} sub={`${stats.not_yet_contacted} left`} />
        <StatCell label="Replied" value={stats.replied} color="emerald" />
        <StatCell label="Reply rate" value={`${stats.reply_rate_pct}%`} color="orange" />
        <StatCell label="Open rate" value={`${stats.open_rate_pct}%`} sub={`click ${stats.click_rate_pct}%`} />
        <StatCell label="Follow-ups queued" value={stats.followups.pending} sub={`${stats.followups.sent} sent`} />
        <StatCell label="Demos booked" value={stats.calls?.demos_booked ?? 0} sub={`of ${stats.calls?.total ?? 0} calls`} color="emerald" />
        <StatCell label="Call win rate" value={`${stats.calls?.win_rate_pct ?? 0}%`} sub={`demo rate ${stats.calls?.demo_rate_pct ?? 0}%`} color="orange" />
      </div>
    </div>
  );
}

function StatCell({ label, value, sub, color }) {
  const cls = color === "emerald" ? "text-emerald-400" :
              color === "orange" ? "text-[#FF5A1F]" : "text-white";
  return (
    <div className="p-3 rounded-lg bg-white/[0.02] border border-white/[0.06]">
      <div className={`text-2xl font-display font-semibold ${cls}`}>{value ?? 0}</div>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">{label}</div>
      {sub && <div className="text-[10px] text-neutral-600 mt-0.5">{sub}</div>}
    </div>
  );
}

// ==================== BATCH REVIEW MODAL ====================
function BatchReviewModal({ drafts, replyTo, onClose, onSent }) {
  // Each draft can be edited or excluded before sending
  const [items, setItems] = useState(() =>
    drafts.map((d) => ({
      lead_id: d.lead_id,
      ok: d.ok,
      error: d.error,
      subject: d.subject || "",
      body: d.body || "",
      company: d.company || "",
      lead_email: d.lead_email || "",
      trucks: d.trucks || 0,
      state: d.state || "",
      included: d.ok,  // exclude failed drafts by default
    }))
  );
  const [expandedIdx, setExpandedIdx] = useState(null);
  const [enableFollowups, setEnableFollowups] = useState(true);
  const [sendMorning, setSendMorning] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendResults, setSendResults] = useState(null);

  const toggleInclude = (idx) => {
    setItems((prev) => prev.map((it, i) => i === idx ? { ...it, included: !it.included } : it));
  };
  const updateSubject = (idx, val) => {
    setItems((prev) => prev.map((it, i) => i === idx ? { ...it, subject: val } : it));
  };
  const updateBody = (idx, val) => {
    setItems((prev) => prev.map((it, i) => i === idx ? { ...it, body: val } : it));
  };

  const includedCount = items.filter((i) => i.included && i.ok).length;

  const handleSendAll = async () => {
    if (includedCount === 0) {
      toast.error("Include at least one email");
      return;
    }
    setSending(true);
    try {
      const payload = {
        emails: items.filter((i) => i.included && i.ok).map((i) => ({
          lead_id: i.lead_id, subject: i.subject, body: i.body,
        })),
        enable_followups: enableFollowups,
        reply_to: replyTo,
        send_mode: sendMorning ? "morning" : "now",
      };
      const { data } = await client.post("/admin/leads/batch-send", payload);
      setSendResults(data);
      if (data.queued > 0) {
        toast.success(`${data.queued} email${data.queued === 1 ? "" : "s"} queued — each delivers at the lead's local morning (8–11am, Tue–Thu)`);
      }
      if (data.sent > 0) {
        toast.success(`${data.sent} email${data.sent === 1 ? "" : "s"} sent!${data.followups_scheduled ? ` ${data.followups_scheduled} follow-ups scheduled.` : ""}`);
      }
      if (data.failed > 0) {
        // If any failure looks like the "Gmail broken" pattern, offer one-click
        // disconnect so the retry sails through Resend
        const gmailBroken = (data.results || []).some(
          (r) => r?.need_gmail_reconnect || (r?.error || "").toLowerCase().includes("gmail rejected")
        );
        if (gmailBroken) {
          if (window.confirm(
            "Gmail app password is broken — that's why some sends failed.\n\n" +
            "Click OK to disconnect Gmail and retry through the shared FreightPilot inbox (Resend)."
          )) {
            try {
              await client.delete("/settings/gmail-smtp");
              toast.success("Gmail disconnected. Click 'Send All' again to retry.");
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Couldn't disconnect Gmail");
            }
          }
        } else {
          toast.error(`${data.failed} failed — check details in modal`);
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Batch send failed");
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    if (sendResults?.sent > 0 || sendResults?.queued > 0) onSent(); else onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={handleClose}
      data-testid="batch-review-modal-backdrop"
    >
      <div
        className="bg-[#0A0A0A] border border-white/10 rounded-xl max-w-3xl w-full max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="batch-review-modal"
      >
        <div className="p-5 sm:p-6 border-b border-white/10 flex items-start justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#FF5A1F] font-semibold mb-1">BATCH OUTREACH REVIEW</div>
            <h2 className="font-display text-xl font-semibold text-white">
              Review {items.length} drafted emails
            </h2>
            <p className="text-neutral-400 text-xs mt-1">
              Click any row to edit. Uncheck to skip. {includedCount} will be sent.
            </p>
          </div>
          <button onClick={handleClose} className="text-neutral-500 hover:text-white p-1" data-testid="batch-review-close">
            <XIcon size={20} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 divide-y divide-white/5">
          {items.map((it, idx) => {
            const result = sendResults?.results?.find((r) => r.lead_id === it.lead_id);
            return (
              <div key={it.lead_id} className={`p-4 sm:p-5 ${!it.ok ? "opacity-50" : ""}`} data-testid={`batch-review-item-${idx}`}>
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={it.included}
                    onChange={() => toggleInclude(idx)}
                    disabled={!it.ok || sending || sendResults}
                    className="w-4 h-4 mt-1 accent-[#FF5A1F] cursor-pointer disabled:cursor-not-allowed"
                    data-testid={`batch-review-toggle-${idx}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-white truncate">{it.company || "(no company)"}</span>
                      {it.trucks > 0 && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/[0.05] text-neutral-400 border border-white/10">
                          {it.trucks} trucks · {it.state}
                        </span>
                      )}
                      {result?.status === "sent" && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 font-semibold">
                          ✓ SENT
                        </span>
                      )}
                      {result?.status === "queued" && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-sky-500/15 text-sky-400 border border-sky-500/30 font-semibold">
                          ⏰ QUEUED — LOCAL MORNING
                        </span>
                      )}
                      {result?.status === "failed" && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-500/15 text-red-400 border border-red-500/30 font-semibold">
                          ✗ FAILED
                        </span>
                      )}
                      {!it.ok && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-500/15 text-red-400 border border-red-500/30 font-semibold">
                          DRAFT FAILED
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-neutral-500 mt-0.5 truncate">{it.lead_email}</div>
                    {it.ok ? (
                      expandedIdx === idx ? (
                        <div className="mt-3 space-y-2">
                          <input
                            type="text"
                            value={it.subject}
                            onChange={(e) => updateSubject(idx, e.target.value)}
                            className="w-full text-sm bg-white/[0.03] border border-white/10 rounded px-3 py-2 text-white outline-none focus:border-[#FF5A1F]/50"
                            data-testid={`batch-review-subject-${idx}`}
                          />
                          <textarea
                            value={it.body}
                            onChange={(e) => updateBody(idx, e.target.value)}
                            rows={8}
                            className="w-full text-sm bg-white/[0.03] border border-white/10 rounded px-3 py-2 text-neutral-200 outline-none focus:border-[#FF5A1F]/50 font-mono leading-relaxed"
                            data-testid={`batch-review-body-${idx}`}
                          />
                          <button
                            onClick={() => setExpandedIdx(null)}
                            className="text-xs text-neutral-400 hover:text-white"
                          >
                            Collapse
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setExpandedIdx(idx)}
                          className="mt-2 text-left w-full"
                          data-testid={`batch-review-expand-${idx}`}
                        >
                          <div className="text-xs font-semibold text-neutral-300 truncate">{it.subject}</div>
                          <div className="text-xs text-neutral-500 line-clamp-2 mt-0.5 leading-relaxed">{it.body.slice(0, 200)}…</div>
                          <div className="text-[10px] text-[#FF5A1F] mt-1.5">Click to edit ↓</div>
                        </button>
                      )
                    ) : (
                      <div className="text-xs text-red-400 mt-2">Error: {it.error}</div>
                    )}
                    {result?.error && (
                      <div className="text-xs text-red-400 mt-2">Send error: {result.error}</div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="p-5 border-t border-white/10 bg-white/[0.02]">
          {!sendResults ? (
            <>
              <label className="flex items-start gap-3 mb-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={sendMorning}
                  onChange={(e) => setSendMorning(e.target.checked)}
                  className="w-4 h-4 mt-0.5 accent-[#FF5A1F] cursor-pointer"
                  data-testid="batch-review-send-morning"
                />
                <div>
                  <div className="text-sm font-medium text-white">⏰ Best send time — deliver at each lead's local morning</div>
                  <div className="text-xs text-neutral-500 mt-0.5">
                    Queues each email for the lead's 8–11am local window (Tue–Thu) when owners actually read inboxes. Uncheck to send immediately.
                  </div>
                </div>
              </label>
              <label className="flex items-start gap-3 mb-4 cursor-pointer">
                <input
                  type="checkbox"
                  checked={enableFollowups}
                  onChange={(e) => setEnableFollowups(e.target.checked)}
                  className="w-4 h-4 mt-0.5 accent-[#FF5A1F] cursor-pointer"
                  data-testid="batch-review-enable-followups"
                />
                <div>
                  <div className="text-sm font-medium text-white">Enable 3-step follow-up sequence</div>
                  <div className="text-xs text-neutral-500 mt-0.5">
                    Auto-sends bump (day 4), value insight (day 10), and final ping (day 21) — cancels if lead replies. 2-3x higher reply rate.
                  </div>
                </div>
              </label>
              <div className="flex items-center justify-between gap-3">
                <div className="text-xs text-neutral-500">
                  Sending from: <span className="text-neutral-300">{replyTo}</span>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleClose}
                    disabled={sending}
                    className="rounded-full px-4 py-2 text-xs border border-white/10 hover:border-white/25 text-neutral-300 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSendAll}
                    disabled={sending || includedCount === 0}
                    data-testid="batch-review-send-all"
                    className="rounded-full px-5 py-2 text-sm font-semibold bg-[#FF5A1F] text-black hover:bg-[#FF7038] disabled:opacity-60 transition-colors inline-flex items-center gap-2"
                  >
                    {sending ? "Sending…" : `Send ${includedCount} emails →`}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-2">
              <div className="text-lg text-white font-semibold mb-1">
                {sendResults.sent} sent · {sendResults.failed} failed
              </div>
              {sendResults.followups_scheduled > 0 && (
                <div className="text-sm text-neutral-400">
                  {sendResults.followups_scheduled} follow-ups scheduled over the next 21 days
                </div>
              )}
              <button
                onClick={handleClose}
                data-testid="batch-review-done"
                className="mt-4 rounded-full px-6 py-2.5 text-sm font-semibold bg-[#FF5A1F] text-black hover:bg-[#FF7038] transition-colors"
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
